// ═══════════════════════════════════════════════════════════
// GAB Components — native GAB Design System UI primitives
// Pure React + GAB CSS variables. Zero Mantine dependency.
// ═══════════════════════════════════════════════════════════

import React, {
  useState, useEffect, useRef, useCallback,
  type ReactNode, type CSSProperties, type HTMLAttributes,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';

// ── Spacing helpers ──────────────────────────────────────

type MantineSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | (string & {}) | number | undefined;

const SPACE: Record<string, string> = {
  xs: '0.25rem', sm: '0.5rem', md: '1rem', lg: '1.5rem', xl: '2rem',
  '0': '0', '1': '0.25rem', '2': '0.5rem', '3': '0.75rem', '4': '1rem',
  '5': '1.25rem', '6': '1.5rem', '8': '2rem', '10': '2.5rem', '12': '3rem',
  '16': '4rem', '20': '5rem', '24': '6rem', 'auto': 'auto',
};

function sp(val: MantineSize): string | undefined {
  if (val === undefined || val === null) return undefined;
  if (typeof val === 'number') return `${val * 0.25}rem`;
  return SPACE[String(val)] ?? val;
}

// ── Color mappings ───────────────────────────────────────

const BADGE_COLOR: Record<string, string> = {
  blue: 'badge-blue', red: 'badge-red', green: 'badge-green', gray: 'badge-gray',
  orange: 'badge-orange', teal: 'badge-teal', violet: 'badge-violet',
  yellow: 'badge-yellow', cyan: 'badge-cyan', indigo: 'badge-indigo',
  pink: 'badge-pink', lime: 'badge-lime', dark: 'badge-dark',
  grape: 'badge-violet', primary: 'badge-primary', secondary: 'badge-secondary',
  success: 'badge-success', warning: 'badge-warning', error: 'badge-error',
};

const BTN_COLOR: Record<string, string> = {
  blue: 'btn-blue', red: 'btn-red', green: 'btn-green', gray: 'btn-gray',
  orange: 'btn-orange', teal: 'btn-teal', violet: 'btn-violet',
  grape: 'btn-violet', yellow: 'btn-warning', cyan: 'btn-teal',
  pink: 'btn-red', indigo: 'btn-blue', lime: 'btn-green', dark: 'btn-secondary',
};

const TEXT_COLOR: Record<string, string> = {
  dimmed: 'var(--color-neutral-500)', red: 'var(--color-error)',
  green: 'var(--color-success)', blue: 'var(--color-primary-600)',
  orange: '#d97706', teal: '#0d9488', violet: '#7c3aed', gray: 'var(--color-neutral-600)',
  white: 'white', black: 'black', yellow: '#d97706',
  'red.6': 'var(--color-error)', 'green.6': 'var(--color-success)',
  'blue.6': 'var(--color-primary-600)',
};

function resolveFontSize(size: MantineSize): string | undefined {
  const map: Record<string, string> = {
    xs: 'var(--font-size-xs)', sm: 'var(--font-size-sm)', md: 'var(--font-size-base)',
    lg: 'var(--font-size-lg)', xl: 'var(--font-size-xl)',
    h1: 'var(--font-size-4xl)', h2: 'var(--font-size-3xl)', h3: 'var(--font-size-2xl)',
    h4: 'var(--font-size-xl)', h5: 'var(--font-size-lg)', h6: 'var(--font-size-base)',
    'compact-xs': 'var(--font-size-xs)', 'compact-sm': 'var(--font-size-sm)',
  };
  if (!size) return undefined;
  return map[String(size)] ?? undefined;
}

// ── GabProvider (root wrapper, no-op) ────────────────────

export function GabProvider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
// Legacy alias — remove once all imports updated
export { GabProvider as MantineProvider };

// ── Button ───────────────────────────────────────────────

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'filled' | 'outline' | 'subtle' | 'light' | 'default' | 'transparent' | 'white' | (string & {});
  size?: MantineSize;
  color?: string;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  leftIcon?: ReactNode;   // Mantine v6 compat
  rightIcon?: ReactNode;  // Mantine v6 compat
  loading?: boolean;
  fullWidth?: boolean;
  radius?: MantineSize;
  justify?: CSSProperties['justifyContent'];
  // spacing shorthands
  mt?: MantineSize; mb?: MantineSize; ml?: MantineSize; mr?: MantineSize;
  mx?: MantineSize; my?: MantineSize; m?: MantineSize;
  pl?: MantineSize; pr?: MantineSize; pt?: MantineSize; pb?: MantineSize;
  px?: MantineSize; py?: MantineSize; p?: MantineSize;
  w?: MantineSize; h?: MantineSize; miw?: MantineSize;
  component?: string | React.ElementType;
  href?: string;
  target?: string;
  compact?: boolean;
  fz?: MantineSize;
  fw?: number | string;
  ta?: CSSProperties['textAlign'];
  display?: string;
  flex?: string | number;
  visibleFrom?: string;
  hiddenFrom?: string;
  c?: string;
  bg?: string;
  bd?: string;
  className?: string;
  download?: boolean | string;
  styles?: Record<string, unknown>;
  gradient?: { from: string; to: string; deg?: number };
  tt?: CSSProperties['textTransform'];
  fs?: CSSProperties['fontStyle'];
  lts?: number | string;
}

function resolveButtonClasses(variant: string | undefined, color: string | undefined, size: MantineSize, compact?: boolean): string {
  let varCls = 'btn-primary';
  if (variant === 'outline') varCls = 'btn-outline';
  else if (variant === 'subtle' || variant === 'transparent') varCls = 'btn-ghost';
  else if (variant === 'light') varCls = 'btn-outline';
  else if (variant === 'default' || variant === 'white') varCls = 'btn-outline';
  else if (variant === 'filled' || !variant) {
    varCls = color ? (BTN_COLOR[color] ?? 'btn-primary') : 'btn-primary';
  }

  let sizeCls = '';
  if (size === 'xs' || size === 'compact-xs') sizeCls = 'btn-sm';
  else if (size === 'sm' || size === 'compact-sm') sizeCls = 'btn-sm';
  else if (size === 'lg') sizeCls = 'btn-lg';
  else if (size === 'xl') sizeCls = 'btn-lg';
  if (compact) sizeCls = 'btn-sm';

  return ['btn', varCls, sizeCls].filter(Boolean).join(' ');
}

const ButtonBase = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  function Button({
    variant, size, color, leftSection, rightSection, leftIcon, rightIcon,
    loading, fullWidth, children, style, className = '', disabled,
    mt, mb, ml, mr, mx, my, m, pl, pr, pt, pb, px, py, p, w, h, miw,
    component, href, target, compact, fz, fw, ta, display, flex, justify,
    visibleFrom: _v, hiddenFrom: _hf, c, bg, bd,
    download: _download, styles: _styles, gradient: _gradient, tt: _tt, fs: _fs, lts: _lts,
    ...rest
  }, _ref) {
    const cls = [resolveButtonClasses(variant, color, size, compact), className, fullWidth ? 'w-full' : ''].filter(Boolean).join(' ');
    const s: CSSProperties = {
      ...(mt ? { marginTop: sp(mt) } : {}),
      ...(mb ? { marginBottom: sp(mb) } : {}),
      ...(ml ? { marginLeft: sp(ml) } : {}),
      ...(mr ? { marginRight: sp(mr) } : {}),
      ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
      ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
      ...(m ? { margin: sp(m) } : {}),
      ...(pl ? { paddingLeft: sp(pl) } : {}),
      ...(pr ? { paddingRight: sp(pr) } : {}),
      ...(pt ? { paddingTop: sp(pt) } : {}),
      ...(pb ? { paddingBottom: sp(pb) } : {}),
      ...(px ? { paddingLeft: sp(px), paddingRight: sp(px) } : {}),
      ...(py ? { paddingTop: sp(py), paddingBottom: sp(py) } : {}),
      ...(p ? { padding: sp(p) } : {}),
      ...(justify ? { justifyContent: justify } : {}),
      ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
      ...(h ? { height: typeof h === 'number' ? `${h}px` : h } : {}),
      ...(miw ? { minWidth: sp(miw) } : {}),
      ...(fullWidth ? { width: '100%' } : {}),
      ...(fz ? { fontSize: resolveFontSize(fz) } : {}),
      ...(fw ? { fontWeight: fw } : {}),
      ...(ta ? { textAlign: ta } : {}),
      ...(display ? { display } : {}),
      ...(flex ? { flex } : {}),
      ...(c ? { color: TEXT_COLOR[c] ?? c } : {}),
      ...(bg ? { backgroundColor: bg } : {}),
      ...(bd ? { border: bd } : {}),
      ...style,
    };
    const left = leftSection ?? leftIcon;
    const right = rightSection ?? rightIcon;
    if (component === 'a' || href) {
      return (
        <a href={href} target={target} className={cls} style={s} {...(rest as any)}>
          {loading && <span className="spinner spinner-sm" style={{ width: 14, height: 14, borderWidth: 2 }} />}
          {!loading && left}
          {children}
          {right}
        </a>
      );
    }
    return (
      <button className={cls} disabled={disabled || loading} style={s} {...rest} ref={_ref as React.Ref<HTMLButtonElement>}>
        {loading && <span className="spinner spinner-sm" style={{ width: 14, height: 14, borderWidth: 2 }} />}
        {!loading && left}
        {children}
        {right}
      </button>
    );
  }
);

// ── ActionIcon ───────────────────────────────────────────

interface ActionIconProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: MantineSize;
  variant?: string;
  color?: string;
  radius?: MantineSize;
  loading?: boolean;
  mt?: MantineSize; mb?: MantineSize; ml?: MantineSize; mr?: MantineSize;
  mx?: MantineSize; my?: MantineSize;
  title?: string;
  component?: string | React.ElementType;
  href?: string;
  target?: string;
  bd?: string;
  c?: string;
  bg?: string;
  flex?: string | number;
  pos?: CSSProperties['position'];
  top?: MantineSize; right?: MantineSize; bottom?: MantineSize; left?: MantineSize;
  w?: MantineSize; h?: MantineSize;
}

export const ActionIcon = React.forwardRef<HTMLButtonElement, ActionIconProps>(
  function ActionIcon({
    size, variant, color, loading, children, style, className = '',
    disabled, mt, mb, ml, mr, mx, my, title, component, href, target,
    bd, c, bg, flex, pos, top, right, bottom, left, w, h, ...rest
  }, ref) {
    let sizeCls = 'btn-icon';
    if (size === 'xs') sizeCls = 'btn-icon-sm';
    else if (size === 'sm') sizeCls = 'btn-icon-sm';
    else if (size === 'lg') sizeCls = 'btn-icon-lg';
    else if (size === 'xl') sizeCls = 'btn-icon-xl';
    else if (typeof size === 'number' && size <= 20) sizeCls = 'btn-icon-sm';
    else if (typeof size === 'number' && size >= 36) sizeCls = 'btn-icon-lg';

    let varCls = 'btn-ghost';
    if (variant === 'filled') varCls = color ? (BTN_COLOR[color] ?? 'btn-primary') : 'btn-primary';
    else if (variant === 'outline') varCls = 'btn-outline';
    else if (variant === 'light') varCls = 'btn-outline';
    else if (variant === 'default') varCls = 'btn-outline';

    const cls = ['btn', varCls, sizeCls, className].filter(Boolean).join(' ');
    const s: CSSProperties = {
      ...(mt ? { marginTop: sp(mt) } : {}),
      ...(mb ? { marginBottom: sp(mb) } : {}),
      ...(ml ? { marginLeft: sp(ml) } : {}),
      ...(mr ? { marginRight: sp(mr) } : {}),
      ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
      ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
      ...(bd ? { border: bd } : {}),
      ...(c ? { color: TEXT_COLOR[c] ?? c } : {}),
      ...(bg ? { backgroundColor: bg } : {}),
      ...(flex ? { flex } : {}),
      ...(pos ? { position: pos } : {}),
      ...(top !== undefined ? { top: sp(top) } : {}),
      ...(right !== undefined ? { right: sp(right) } : {}),
      ...(bottom !== undefined ? { bottom: sp(bottom) } : {}),
      ...(left !== undefined ? { left: sp(left) } : {}),
      ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
      ...(h ? { height: typeof h === 'number' ? `${h}px` : h } : {}),
      ...style,
    };
    if (href) {
      return <a href={href} target={target} className={cls} style={s} title={title} {...(rest as any)}>{children}</a>;
    }
    return (
      <button ref={ref} className={cls} disabled={disabled || loading} style={s} title={title} {...rest}>
        {loading ? <span className="spinner spinner-sm" style={{ width: 14, height: 14, borderWidth: 2 }} /> : children}
      </button>
    );
  }
);

// ── Text ─────────────────────────────────────────────────

interface TextProps extends HTMLAttributes<HTMLElement> {
  size?: MantineSize;
  c?: string;
  fw?: number | string;
  ta?: CSSProperties['textAlign'];
  fz?: MantineSize;
  lh?: string | number;
  truncate?: boolean | 'start' | 'end';
  lineClamp?: number;
  inline?: boolean;
  inherit?: boolean;
  span?: boolean;
  ff?: string;
  mt?: MantineSize; mb?: MantineSize; ml?: MantineSize; mr?: MantineSize;
  mx?: MantineSize; my?: MantineSize; m?: MantineSize;
  pt?: MantineSize; pb?: MantineSize; pl?: MantineSize; pr?: MantineSize;
  px?: MantineSize; py?: MantineSize; p?: MantineSize;
  w?: MantineSize; maw?: MantineSize; miw?: MantineSize;
  component?: string | React.ElementType;
  display?: string;
  flex?: string | number;
  opacity?: number | string;
  td?: string;
  tt?: CSSProperties['textTransform'];
  fs?: CSSProperties['fontStyle'];
  href?: string;
  target?: string;
  lts?: number | string;
}

export function Text({
  size, c, fw, ta, fz, lh, truncate, lineClamp, inline, inherit: _inherit, span, ff,
  mt, mb, ml, mr, mx, my, m, pt, pb, pl, pr, px, py, p, w, maw, miw,
  component, display, flex, opacity, td, tt, fs, href, target, lts,
  style, className = '', children, ...rest
}: TextProps) {
  const s: CSSProperties = {
    ...(size ? { fontSize: resolveFontSize(size) } : {}),
    ...(fz ? { fontSize: resolveFontSize(fz) } : {}),
    ...(c ? { color: TEXT_COLOR[c] ?? c } : {}),
    ...(fw ? { fontWeight: fw } : {}),
    ...(ta ? { textAlign: ta } : {}),
    ...(lh ? { lineHeight: lh } : {}),
    ...(truncate ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
    ...(lineClamp ? { display: '-webkit-box', WebkitLineClamp: lineClamp, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}),
    ...(ff ? { fontFamily: ff } : {}),
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(ml ? { marginLeft: sp(ml) } : {}),
    ...(mr ? { marginRight: sp(mr) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(m ? { margin: sp(m) } : {}),
    ...(pt ? { paddingTop: sp(pt) } : {}),
    ...(pb ? { paddingBottom: sp(pb) } : {}),
    ...(pl ? { paddingLeft: sp(pl) } : {}),
    ...(pr ? { paddingRight: sp(pr) } : {}),
    ...(px ? { paddingLeft: sp(px), paddingRight: sp(px) } : {}),
    ...(py ? { paddingTop: sp(py), paddingBottom: sp(py) } : {}),
    ...(p ? { padding: sp(p) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(maw ? { maxWidth: typeof maw === 'number' ? `${maw}px` : maw } : {}),
    ...(miw ? { minWidth: typeof miw === 'number' ? `${miw}px` : miw } : {}),
    ...(display ? { display } : {}),
    ...(flex ? { flex } : {}),
    ...(opacity !== undefined ? { opacity: Number(opacity) } : {}),
    ...(td ? { textDecoration: td } : {}),
    ...(tt ? { textTransform: tt } : {}),
    ...(fs ? { fontStyle: fs } : {}),
    ...(lts !== undefined ? { letterSpacing: typeof lts === 'number' ? `${lts}px` : lts } : {}),
    ...style,
  };
  const Tag = (component as any) ?? (span || inline ? 'span' : 'p');
  return <Tag className={className} style={s} href={href} target={target} {...rest}>{children}</Tag>;
}

// ── Title ─────────────────────────────────────────────────

interface TitleProps extends HTMLAttributes<HTMLHeadingElement> {
  order?: 1 | 2 | 3 | 4 | 5 | 6;
  size?: MantineSize;
  ta?: CSSProperties['textAlign'];
  c?: string;
  fw?: number | string;
  fz?: MantineSize;
  mt?: MantineSize; mb?: MantineSize; ml?: MantineSize; mr?: MantineSize;
  mx?: MantineSize; my?: MantineSize; m?: MantineSize;
  pt?: MantineSize; pb?: MantineSize; px?: MantineSize; py?: MantineSize; p?: MantineSize;
  w?: MantineSize;
}

export function Title({
  order = 2, size, ta, c, fw, fz, mt, mb, ml, mr, mx, my, m, pt, pb, px, py, p, w,
  style, children, ...rest
}: TitleProps) {
  const Tag = `h${order}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  const s: CSSProperties = {
    ...(size ? { fontSize: resolveFontSize(size) } : {}),
    ...(fz ? { fontSize: resolveFontSize(fz) } : {}),
    ...(ta ? { textAlign: ta } : {}),
    ...(c ? { color: TEXT_COLOR[c] ?? c } : {}),
    ...(fw ? { fontWeight: fw } : {}),
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(ml ? { marginLeft: sp(ml) } : {}),
    ...(mr ? { marginRight: sp(mr) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(m ? { margin: sp(m) } : {}),
    ...(pt ? { paddingTop: sp(pt) } : {}),
    ...(pb ? { paddingBottom: sp(pb) } : {}),
    ...(px ? { paddingLeft: sp(px), paddingRight: sp(px) } : {}),
    ...(py ? { paddingTop: sp(py), paddingBottom: sp(py) } : {}),
    ...(p ? { padding: sp(p) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...style,
  };
  return <Tag style={s} {...rest}>{children}</Tag>;
}

// ── Group ─────────────────────────────────────────────────

interface GroupProps extends HTMLAttributes<HTMLDivElement> {
  gap?: MantineSize;
  align?: CSSProperties['alignItems'];
  justify?: CSSProperties['justifyContent'];
  wrap?: CSSProperties['flexWrap'];
  grow?: boolean;
  mt?: MantineSize; mb?: MantineSize; ml?: MantineSize; mr?: MantineSize;
  mx?: MantineSize; my?: MantineSize; m?: MantineSize;
  pt?: MantineSize; pb?: MantineSize; px?: MantineSize; py?: MantineSize; p?: MantineSize;
  w?: MantineSize; miw?: MantineSize; maw?: MantineSize;
  h?: MantineSize;
  display?: string;
  flex?: string | number;
  pos?: CSSProperties['position'];
  top?: MantineSize; right?: MantineSize; bottom?: MantineSize; left?: MantineSize;
  opacity?: number | string;
  bd?: string;
  bg?: string;
  c?: string;
  fz?: MantineSize;
  fw?: number | string;
  ta?: CSSProperties['textAlign'];
  preventGrowOverflow?: boolean;
  visibleFrom?: string;
  hiddenFrom?: string;
}

export function Group({
  gap = 'sm', align = 'center', justify, wrap, grow, children,
  mt, mb, ml, mr, mx, my, m, pt, pb, px, py, p, w, miw, maw, h,
  display, flex, pos, top, right, bottom, left, opacity, bd, bg, c, fz, fw, ta,
  preventGrowOverflow: _pg, visibleFrom: _v, hiddenFrom: _hf,
  style, className = '', ...rest
}: GroupProps) {
  const s: CSSProperties = {
    display: display ?? 'flex',
    flexDirection: 'row',
    alignItems: align,
    ...(justify ? { justifyContent: justify } : {}),
    ...(wrap ? { flexWrap: wrap } : {}),
    ...(gap !== undefined ? { gap: sp(gap) } : {}),
    ...(grow ? { '& > *': { flex: 1 } } : {}),
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(ml ? { marginLeft: sp(ml) } : {}),
    ...(mr ? { marginRight: sp(mr) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(m ? { margin: sp(m) } : {}),
    ...(pt ? { paddingTop: sp(pt) } : {}),
    ...(pb ? { paddingBottom: sp(pb) } : {}),
    ...(px ? { paddingLeft: sp(px), paddingRight: sp(px) } : {}),
    ...(py ? { paddingTop: sp(py), paddingBottom: sp(py) } : {}),
    ...(p ? { padding: sp(p) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(miw ? { minWidth: typeof miw === 'number' ? `${miw}px` : miw } : {}),
    ...(maw ? { maxWidth: typeof maw === 'number' ? `${maw}px` : maw } : {}),
    ...(h ? { height: typeof h === 'number' ? `${h}px` : h } : {}),
    ...(flex !== undefined ? { flex } : {}),
    ...(pos ? { position: pos } : {}),
    ...(top !== undefined ? { top: sp(top) } : {}),
    ...(right !== undefined ? { right: sp(right) } : {}),
    ...(bottom !== undefined ? { bottom: sp(bottom) } : {}),
    ...(left !== undefined ? { left: sp(left) } : {}),
    ...(opacity !== undefined ? { opacity: Number(opacity) } : {}),
    ...(bd ? { border: bd } : {}),
    ...(bg ? { backgroundColor: bg } : {}),
    ...(c ? { color: TEXT_COLOR[c] ?? c } : {}),
    ...(fz ? { fontSize: resolveFontSize(fz) } : {}),
    ...(fw ? { fontWeight: fw } : {}),
    ...(ta ? { textAlign: ta } : {}),
    ...style,
  };
  return <div className={className} style={s} {...rest}>{children}</div>;
}

// ── Stack ─────────────────────────────────────────────────

interface StackProps extends HTMLAttributes<HTMLDivElement> {
  gap?: MantineSize;
  align?: CSSProperties['alignItems'];
  justify?: CSSProperties['justifyContent'];
  mt?: MantineSize; mb?: MantineSize; ml?: MantineSize; mr?: MantineSize;
  mx?: MantineSize; my?: MantineSize; m?: MantineSize;
  pt?: MantineSize; pb?: MantineSize; pl?: MantineSize; pr?: MantineSize; px?: MantineSize; py?: MantineSize; p?: MantineSize;
  w?: MantineSize; miw?: MantineSize; maw?: MantineSize; h?: MantineSize; mah?: MantineSize;
  display?: string;
  flex?: string | number;
  pos?: CSSProperties['position'];
  top?: MantineSize; right?: MantineSize; bottom?: MantineSize; left?: MantineSize;
  bg?: string; c?: string; bd?: string; opacity?: number | string;
  fz?: MantineSize; fw?: number | string; ta?: CSSProperties['textAlign'];
  visibleFrom?: string; hiddenFrom?: string;
}

export function Stack({
  gap = 'md', align, justify, children,
  mt, mb, ml, mr, mx, my, m, pt, pb, pl, pr, px, py, p, w, miw, maw, h, mah,
  display, flex, pos, top, right, bottom, left, bg, c, bd, opacity, fz, fw, ta,
  visibleFrom: _v, hiddenFrom: _hf,
  style, className = '', ...rest
}: StackProps) {
  const s: CSSProperties = {
    display: display ?? 'flex',
    flexDirection: 'column',
    ...(gap !== undefined ? { gap: sp(gap) } : {}),
    ...(align ? { alignItems: align } : {}),
    ...(justify ? { justifyContent: justify } : {}),
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(ml ? { marginLeft: sp(ml) } : {}),
    ...(mr ? { marginRight: sp(mr) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(m ? { margin: sp(m) } : {}),
    ...(pt ? { paddingTop: sp(pt) } : {}),
    ...(pb ? { paddingBottom: sp(pb) } : {}),
    ...(pl ? { paddingLeft: sp(pl) } : {}),
    ...(pr ? { paddingRight: sp(pr) } : {}),
    ...(px ? { paddingLeft: sp(px), paddingRight: sp(px) } : {}),
    ...(py ? { paddingTop: sp(py), paddingBottom: sp(py) } : {}),
    ...(p ? { padding: sp(p) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(miw ? { minWidth: typeof miw === 'number' ? `${miw}px` : miw } : {}),
    ...(maw ? { maxWidth: typeof maw === 'number' ? `${maw}px` : maw } : {}),
    ...(h ? { height: typeof h === 'number' ? `${h}px` : h } : {}),
    ...(mah ? { maxHeight: typeof mah === 'number' ? `${mah}px` : mah } : {}),
    ...(flex !== undefined ? { flex } : {}),
    ...(pos ? { position: pos } : {}),
    ...(top !== undefined ? { top: sp(top) } : {}),
    ...(right !== undefined ? { right: sp(right) } : {}),
    ...(bottom !== undefined ? { bottom: sp(bottom) } : {}),
    ...(left !== undefined ? { left: sp(left) } : {}),
    ...(bg ? { backgroundColor: bg } : {}),
    ...(c ? { color: TEXT_COLOR[c] ?? c } : {}),
    ...(bd ? { border: bd } : {}),
    ...(opacity !== undefined ? { opacity: Number(opacity) } : {}),
    ...(fz ? { fontSize: resolveFontSize(fz) } : {}),
    ...(fw ? { fontWeight: fw } : {}),
    ...(ta ? { textAlign: ta } : {}),
    ...style,
  };
  return <div className={className} style={s} {...rest}>{children}</div>;
}

// ── Paper ─────────────────────────────────────────────────

interface PaperProps extends HTMLAttributes<HTMLDivElement> {
  p?: MantineSize;
  pt?: MantineSize; pb?: MantineSize; px?: MantineSize; py?: MantineSize;
  pl?: MantineSize; pr?: MantineSize;
  mt?: MantineSize; mb?: MantineSize; ml?: MantineSize; mr?: MantineSize;
  mx?: MantineSize; my?: MantineSize; m?: MantineSize;
  withBorder?: boolean;
  shadow?: string;
  radius?: MantineSize;
  bg?: string; c?: string; bd?: string;
  w?: MantineSize; h?: MantineSize; mah?: MantineSize; maw?: MantineSize; miw?: MantineSize;
  pos?: CSSProperties['position'];
  top?: MantineSize; right?: MantineSize; bottom?: MantineSize; left?: MantineSize;
  display?: string; flex?: string | number;
  ta?: CSSProperties['textAlign'];
  fz?: MantineSize; fw?: number | string;
  opacity?: number | string;
  component?: string | React.ElementType;
  ref?: React.Ref<HTMLDivElement>;
}

export function Paper({
  p = 'md', pt, pb, px, py, pl, pr, mt, mb, ml, mr, mx, my, m,
  withBorder, shadow, children,
  bg, c, bd, w, h, mah, maw, miw, pos, top, right, bottom, left,
  display, flex, ta, fz, fw, opacity, component,
  style, className = '', ...rest
}: PaperProps) {
  const Tag = (component as any) ?? 'div';
  const s: CSSProperties = {
    backgroundColor: bg ?? 'white',
    borderRadius: 'var(--radius-lg)',
    ...(withBorder ? { border: bd ?? '1px solid var(--color-neutral-200)' } : bd ? { border: bd } : {}),
    ...(shadow && shadow !== 'none' ? { boxShadow: 'var(--shadow-md)' } : shadow === 'none' ? {} : { boxShadow: 'var(--shadow-md)' }),
    ...(p ? { padding: sp(p) } : {}),
    ...(pt ? { paddingTop: sp(pt) } : {}),
    ...(pb ? { paddingBottom: sp(pb) } : {}),
    ...(px ? { paddingLeft: sp(px), paddingRight: sp(px) } : {}),
    ...(py ? { paddingTop: sp(py), paddingBottom: sp(py) } : {}),
    ...(pl ? { paddingLeft: sp(pl) } : {}),
    ...(pr ? { paddingRight: sp(pr) } : {}),
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(ml ? { marginLeft: sp(ml) } : {}),
    ...(mr ? { marginRight: sp(mr) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(m ? { margin: sp(m) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(h ? { height: typeof h === 'number' ? `${h}px` : h } : {}),
    ...(mah ? { maxHeight: typeof mah === 'number' ? `${mah}px` : mah } : {}),
    ...(maw ? { maxWidth: typeof maw === 'number' ? `${maw}px` : maw } : {}),
    ...(miw ? { minWidth: typeof miw === 'number' ? `${miw}px` : miw } : {}),
    ...(pos ? { position: pos } : {}),
    ...(top !== undefined ? { top: sp(top) } : {}),
    ...(right !== undefined ? { right: sp(right) } : {}),
    ...(bottom !== undefined ? { bottom: sp(bottom) } : {}),
    ...(left !== undefined ? { left: sp(left) } : {}),
    ...(display ? { display } : {}),
    ...(flex !== undefined ? { flex } : {}),
    ...(ta ? { textAlign: ta } : {}),
    ...(fz ? { fontSize: resolveFontSize(fz) } : {}),
    ...(fw ? { fontWeight: fw } : {}),
    ...(opacity !== undefined ? { opacity: Number(opacity) } : {}),
    ...(c ? { color: TEXT_COLOR[c] ?? c } : {}),
    ...style,
  };
  return <Tag className={className} style={s} {...rest}>{children}</Tag>;
}

// ── Badge ─────────────────────────────────────────────────

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  color?: string;
  variant?: 'filled' | 'light' | 'outline' | 'dot' | (string & {});
  size?: MantineSize;
  radius?: MantineSize;
  circle?: boolean;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  mt?: MantineSize; mb?: MantineSize; ml?: MantineSize; mr?: MantineSize;
  mx?: MantineSize; my?: MantineSize; m?: MantineSize;
  pt?: MantineSize; pb?: MantineSize; pl?: MantineSize; pr?: MantineSize;
  px?: MantineSize; py?: MantineSize; p?: MantineSize;
  fz?: MantineSize; fw?: number | string;
  w?: MantineSize; miw?: MantineSize; maw?: MantineSize;
  bd?: string; c?: string; bg?: string;
  tt?: CSSProperties['textTransform'];
  ff?: string;
  styles?: Record<string, unknown>;
  visibleFrom?: string; hiddenFrom?: string;
}

export function Badge({
  color, variant, size, children, circle,
  leftSection, rightSection,
  mt, mb, ml, mr, mx, my, m, pt, pb, pl, pr, px, py, p, fz, fw, w, miw, maw, bd, c, bg, tt,
  ff, styles: _styles,
  visibleFrom: _v, hiddenFrom: _hf,
  style, className = '', ...rest
}: BadgeProps) {
  const colorCls = BADGE_COLOR[color ?? ''] ?? (color ? `badge-${color}` : 'badge-neutral');
  const s: CSSProperties = {
    ...(size === 'xs' ? { fontSize: 'var(--font-size-xs)', padding: '0.1rem 0.4rem' } : {}),
    ...(size === 'sm' ? { fontSize: 'var(--font-size-xs)' } : {}),
    ...(size === 'lg' ? { fontSize: 'var(--font-size-sm)', padding: '0.3rem 0.6rem' } : {}),
    ...(size === 'md' ? { fontSize: 'var(--font-size-xs)' } : {}),
    ...(circle ? { borderRadius: '50%', aspectRatio: '1', padding: '0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' } : {}),
    ...(fz ? { fontSize: resolveFontSize(fz) } : {}),
    ...(fw ? { fontWeight: fw } : {}),
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(ml ? { marginLeft: sp(ml) } : {}),
    ...(mr ? { marginRight: sp(mr) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(m ? { margin: sp(m) } : {}),
    ...(pt ? { paddingTop: sp(pt) } : {}),
    ...(pb ? { paddingBottom: sp(pb) } : {}),
    ...(pl ? { paddingLeft: sp(pl) } : {}),
    ...(pr ? { paddingRight: sp(pr) } : {}),
    ...(px ? { paddingLeft: sp(px), paddingRight: sp(px) } : {}),
    ...(py ? { paddingTop: sp(py), paddingBottom: sp(py) } : {}),
    ...(p ? { padding: sp(p) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(miw ? { minWidth: typeof miw === 'number' ? `${miw}px` : miw } : {}),
    ...(maw ? { maxWidth: typeof maw === 'number' ? `${maw}px` : maw } : {}),
    ...(bd ? { border: bd } : {}),
    ...(c ? { color: TEXT_COLOR[c] ?? c } : {}),
    ...(bg ? { backgroundColor: bg } : {}),
    ...(tt ? { textTransform: tt } : {}),
    ...(ff ? { fontFamily: ff } : {}),
    ...style,
  };
  return (
    <span className={`badge ${colorCls} ${className}`} style={s} {...rest}>
      {leftSection}
      {children}
      {rightSection}
    </span>
  );
}

// ── Center ─────────────────────────────────────────────────

interface CenterProps extends HTMLAttributes<HTMLDivElement> {
  inline?: boolean;
  mt?: MantineSize; mb?: MantineSize; ml?: MantineSize; mr?: MantineSize;
  mx?: MantineSize; my?: MantineSize; m?: MantineSize;
  pt?: MantineSize; pb?: MantineSize; pl?: MantineSize; pr?: MantineSize; px?: MantineSize; py?: MantineSize; p?: MantineSize;
  w?: MantineSize; h?: MantineSize; maw?: MantineSize; mih?: MantineSize;
  flex?: string | number; display?: string;
  c?: string; bg?: string; opacity?: number | string;
}

export function Center({
  inline, children, mt, mb, ml, mr, mx, my, m, pt, pb, pl, pr, px, py, p, w, h, maw, mih,
  flex, display, c, bg, opacity,
  style, className = '', ...rest
}: CenterProps) {
  const s: CSSProperties = {
    display: display ?? (inline ? 'inline-flex' : 'flex'),
    alignItems: 'center',
    justifyContent: 'center',
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(ml ? { marginLeft: sp(ml) } : {}),
    ...(mr ? { marginRight: sp(mr) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(m ? { margin: sp(m) } : {}),
    ...(pt ? { paddingTop: sp(pt) } : {}),
    ...(pb ? { paddingBottom: sp(pb) } : {}),
    ...(pl ? { paddingLeft: sp(pl) } : {}),
    ...(pr ? { paddingRight: sp(pr) } : {}),
    ...(px ? { paddingLeft: sp(px), paddingRight: sp(px) } : {}),
    ...(py ? { paddingTop: sp(py), paddingBottom: sp(py) } : {}),
    ...(p ? { padding: sp(p) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(h ? { height: typeof h === 'number' ? `${h}px` : h } : {}),
    ...(maw ? { maxWidth: typeof maw === 'number' ? `${maw}px` : maw } : {}),
    ...(mih ? { minHeight: typeof mih === 'number' ? `${mih}px` : mih } : {}),
    ...(flex !== undefined ? { flex } : {}),
    ...(c ? { color: TEXT_COLOR[c] ?? c } : {}),
    ...(bg ? { backgroundColor: bg } : {}),
    ...(opacity !== undefined ? { opacity: Number(opacity) } : {}),
    ...style,
  };
  return <div className={className} style={s} {...rest}>{children}</div>;
}

// ── Loader ─────────────────────────────────────────────────

interface LoaderProps {
  size?: MantineSize;
  color?: string;
  type?: string;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  style?: CSSProperties;
  className?: string;
}

export function Loader({ size, mt, mb, mx, my, style, className = '' }: LoaderProps) {
  let spinnerCls = 'spinner';
  if (size === 'xs' || size === 'sm') spinnerCls += ' spinner-sm';
  else if (size === 'lg' || size === 'xl') spinnerCls += ' spinner-lg';
  else if (typeof size === 'number') {
    const dim = size;
    const s2: CSSProperties = {
      width: dim, height: dim,
      ...(mt ? { marginTop: sp(mt) } : {}),
      ...(mb ? { marginBottom: sp(mb) } : {}),
      ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
      ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
      ...style,
    };
    return <span className={`spinner ${className}`} style={s2} />;
  }
  const s: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...style,
  };
  return <span className={`${spinnerCls} ${className}`} style={s} />;
}

// ── LoadingOverlay ─────────────────────────────────────────

interface LoadingOverlayProps {
  visible: boolean;
  overlayProps?: Record<string, unknown>;
  loaderProps?: Record<string, unknown>;
  zIndex?: number;
}

export function LoadingOverlay({ visible }: LoadingOverlayProps) {
  if (!visible) return null;
  return (
    <div className="loading-overlay">
      <span className="spinner" />
    </div>
  );
}

// ── Divider ─────────────────────────────────────────────────

interface DividerProps {
  my?: MantineSize; mt?: MantineSize; mb?: MantineSize;
  mx?: MantineSize; ml?: MantineSize; mr?: MantineSize;
  label?: ReactNode;
  labelPosition?: 'left' | 'center' | 'right';
  color?: string;
  orientation?: 'horizontal' | 'vertical';
  size?: MantineSize;
  style?: CSSProperties;
  className?: string;
  variant?: string;
}

export function Divider({
  my, mt, mb, mx, ml, mr, label, labelPosition = 'center', color,
  orientation = 'horizontal', style, className = '',
}: DividerProps) {
  const s: CSSProperties = {
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(ml ? { marginLeft: sp(ml) } : {}),
    ...(mr ? { marginRight: sp(mr) } : {}),
    ...(color ? { borderColor: color } : {}),
    ...style,
  };
  if (orientation === 'vertical') {
    return <div className={`divider-vertical ${className}`} style={s} />;
  }
  if (label) {
    const textAlign = labelPosition === 'left' ? 'left' : labelPosition === 'right' ? 'right' : 'center';
    return (
      <div className={className} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', ...s }}>
        {labelPosition !== 'left' && <hr className="divider" style={{ flex: 1, margin: 0 }} />}
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', whiteSpace: 'nowrap', textAlign }}>{label}</span>
        {labelPosition !== 'right' && <hr className="divider" style={{ flex: 1, margin: 0 }} />}
      </div>
    );
  }
  return <hr className={`divider ${className}`} style={s} />;
}

// ── Alert ─────────────────────────────────────────────────

interface AlertProps {
  title?: ReactNode;
  icon?: ReactNode;
  color?: string;
  variant?: string;
  withCloseButton?: boolean;
  onClose?: () => void;
  children?: ReactNode;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  p?: MantineSize; py?: MantineSize; px?: MantineSize;
  w?: MantineSize;
  radius?: MantineSize;
  style?: CSSProperties;
  className?: string;
}

export function Alert({
  title, icon, color, children, withCloseButton, onClose,
  mt, mb, mx, my, p, py, px, w, radius: _radius, style, className = '',
}: AlertProps) {
  const colorMap: Record<string, string> = {
    green: 'alert-success', teal: 'alert-success', red: 'alert-error', pink: 'alert-error',
    yellow: 'alert-warning', orange: 'alert-warning', blue: 'alert-info', cyan: 'alert-info',
    gray: '', dark: '',
  };
  const alertCls = color ? (colorMap[color] ?? `alert-${color}`) : 'alert-info';
  const s: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(p ? { padding: sp(p) } : {}),
    ...(py ? { paddingTop: sp(py), paddingBottom: sp(py) } : {}),
    ...(px ? { paddingLeft: sp(px), paddingRight: sp(px) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...style,
  };
  return (
    <div className={`alert ${alertCls} ${className}`} role="alert" style={s}>
      {icon && <span style={{ flexShrink: 0 }}>{icon}</span>}
      <div style={{ flex: 1 }}>
        {title && <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{title}</strong>}
        {children}
      </div>
      {withCloseButton && (
        <button className="alert-dismiss" onClick={onClose} aria-label="Close">&times;</button>
      )}
    </div>
  );
}

// ── SimpleGrid ─────────────────────────────────────────────

const BREAKPOINTS = { xs: 480, sm: 576, md: 768, lg: 992, xl: 1200 };

// Shared resize listeners — single DOM listener total
const _resizeListeners = new Set<() => void>();
let _windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
if (typeof window !== 'undefined') {
  window.addEventListener('resize', () => {
    _windowWidth = window.innerWidth;
    _resizeListeners.forEach(l => l());
  }, { passive: true });
}

function useWindowWidth() {
  const [w, setW] = useState(_windowWidth);
  useEffect(() => {
    const cb = () => setW(_windowWidth);
    _resizeListeners.add(cb);
    return () => { _resizeListeners.delete(cb); };
  }, []);
  return w;
}

function resolveColsFromWidth(
  cols: number | { base?: number; xs?: number; sm?: number; md?: number; lg?: number; xl?: number },
  width: number
): number {
  if (typeof cols === 'number') return cols;
  if (width >= BREAKPOINTS.xl && cols.xl != null) return cols.xl;
  if (width >= BREAKPOINTS.lg && cols.lg != null) return cols.lg;
  if (width >= BREAKPOINTS.md && cols.md != null) return cols.md;
  if (width >= BREAKPOINTS.sm && cols.sm != null) return cols.sm;
  if (width >= BREAKPOINTS.xs && cols.xs != null) return cols.xs;
  return cols.base ?? 1;
}

interface SimpleGridProps extends HTMLAttributes<HTMLDivElement> {
  cols?: number | { base?: number; xs?: number; sm?: number; md?: number; lg?: number; xl?: number };
  spacing?: MantineSize;
  verticalSpacing?: MantineSize;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  m?: MantineSize; p?: MantineSize;
  w?: MantineSize; miw?: MantineSize;
}

export function SimpleGrid({
  cols = 2, spacing = 'md', verticalSpacing, children,
  mt, mb, mx, my, m, p, w, miw,
  style, className = '', ...rest
}: SimpleGridProps) {
  const windowWidth = useWindowWidth();
  const numCols = resolveColsFromWidth(cols, windowWidth);
  const s: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `repeat(${numCols}, minmax(0, 1fr))`,
    gap: sp(spacing) ?? '1rem',
    ...(verticalSpacing ? { rowGap: sp(verticalSpacing) } : {}),
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(m ? { margin: sp(m) } : {}),
    ...(p ? { padding: sp(p) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(miw ? { minWidth: typeof miw === 'number' ? `${miw}px` : miw } : {}),
    ...style,
  };
  return <div className={className} style={s} {...rest}>{children}</div>;
}

// ── TextInput ─────────────────────────────────────────────

interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  size?: MantineSize;
  radius?: MantineSize;
  variant?: string;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  m?: MantineSize; w?: MantineSize; miw?: MantineSize; maw?: MantineSize;
  flex?: string | number;
  styles?: Record<string, unknown>;
  withAsterisk?: boolean;
  fw?: number | string;
  fz?: MantineSize;
  c?: string;
  ta?: CSSProperties['textAlign'];
}

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  function TextInput({
    label, description, error, required, size, variant: _variant, leftSection, rightSection,
    mt, mb, mx, my, m, w, miw, maw, flex, styles: _styles, withAsterisk,
    fw: _fw, fz: _fz, c: _c, ta: _ta,
    style, className = '', id, ...rest
  }, ref) {
    const inputId = id || (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
    const wrapStyle: CSSProperties = {
      ...(mt ? { marginTop: sp(mt) } : {}),
      ...(mb ? { marginBottom: sp(mb) } : {}),
      ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
      ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
      ...(m ? { margin: sp(m) } : {}),
      ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
      ...(miw ? { minWidth: typeof miw === 'number' ? `${miw}px` : miw } : {}),
      ...(maw ? { maxWidth: typeof maw === 'number' ? `${maw}px` : maw } : {}),
      ...(flex !== undefined ? { flex } : {}),
    };
    const inputStyle: CSSProperties = {
      ...(leftSection ? { paddingLeft: '2rem' } : {}),
      ...(rightSection ? { paddingRight: '2rem' } : {}),
      ...style,
    };
    return (
      <div className={`form-group ${className}`} style={wrapStyle}>
        {label && (
          <label className={`form-label${required || withAsterisk ? ' required' : ''}`} htmlFor={inputId}>
            {label}
          </label>
        )}
        {description && <span className="form-help">{description}</span>}
        <div style={{ position: 'relative' }}>
          {leftSection && (
            <span style={{ position: 'absolute', left: '0.5rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-neutral-500)', pointerEvents: 'none' }}>
              {leftSection}
            </span>
          )}
          <input
            ref={ref}
            className={`form-input${error ? ' error' : ''}`}
            id={inputId}
            style={inputStyle}
            {...rest}
          />
          {rightSection && (
            <span style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-neutral-500)' }}>
              {rightSection}
            </span>
          )}
        </div>
        {error && <span className="form-error" role="alert">{error}</span>}
      </div>
    );
  }
);

// ── Textarea ─────────────────────────────────────────────

interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  size?: MantineSize;
  variant?: string;
  autosize?: boolean;
  minRows?: number;
  maxRows?: number;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  m?: MantineSize; w?: MantineSize; flex?: string | number;
  styles?: Record<string, unknown>;
  withAsterisk?: boolean;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  fw?: number | string;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea({
    label, description, error, required, autosize: _a, minRows, maxRows,
    variant: _variant, leftSection: _ls, rightSection: _rs, fw: _fw,
    mt, mb, mx, my, m, w, flex, styles: _styles, withAsterisk,
    style, className = '', id, ...rest
  }, ref) {
    const inputId = id || (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
    const wrapStyle: CSSProperties = {
      ...(mt ? { marginTop: sp(mt) } : {}),
      ...(mb ? { marginBottom: sp(mb) } : {}),
      ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
      ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
      ...(m ? { margin: sp(m) } : {}),
      ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
      ...(flex !== undefined ? { flex } : {}),
    };
    return (
      <div className={`form-group ${className}`} style={wrapStyle}>
        {label && (
          <label className={`form-label${required || withAsterisk ? ' required' : ''}`} htmlFor={inputId}>
            {label}
          </label>
        )}
        {description && <span className="form-help">{description}</span>}
        <textarea
          ref={ref}
          className={`form-textarea${error ? ' error' : ''}`}
          id={inputId}
          rows={minRows}
          style={{ maxHeight: maxRows ? `${maxRows * 1.6}rem` : undefined, ...style }}
          {...rest}
        />
        {error && <span className="form-error" role="alert">{error}</span>}
      </div>
    );
  }
);

// ── PasswordInput ─────────────────────────────────────────

interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  size?: MantineSize;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  w?: MantineSize; flex?: string | number;
  styles?: Record<string, unknown>;
  withAsterisk?: boolean;
}

export function PasswordInput({
  label, description, error, required, mt, mb, mx, my, w, flex, withAsterisk,
  size: _size, styles: _styles,
  style, className = '', id, ...rest
}: PasswordInputProps) {
  const [show, setShow] = useState(false);
  const inputId = id || (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  const wrapStyle: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(flex !== undefined ? { flex } : {}),
  };
  return (
    <div className={`form-group ${className}`} style={wrapStyle}>
      {label && (
        <label className={`form-label${required || withAsterisk ? ' required' : ''}`} htmlFor={inputId}>
          {label}
        </label>
      )}
      {description && <span className="form-help">{description}</span>}
      <div style={{ position: 'relative' }}>
        <input
          type={show ? 'text' : 'password'}
          className={`form-input${error ? ' error' : ''}`}
          id={inputId}
          style={{ paddingRight: '2.5rem', ...style }}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setShow(s => !s)}
          style={{
            position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-neutral-500)',
            padding: '0.25rem',
          }}
          aria-label={show ? 'Hide password' : 'Show password'}
        >
          {show ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          )}
        </button>
      </div>
      {error && <span className="form-error" role="alert">{error}</span>}
    </div>
  );
}

// ── Select ─────────────────────────────────────────────────

type SelectData = string[] | Array<{ value: string; label: string; disabled?: boolean; group?: string }>;

interface SelectProps {
  data: SelectData;
  value?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (value: any) => void;
  label?: ReactNode;
  placeholder?: string;
  searchable?: boolean;
  clearable?: boolean;
  required?: boolean;
  error?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  size?: MantineSize;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  m?: MantineSize; w?: MantineSize; flex?: string | number; miw?: MantineSize; maw?: MantineSize;
  nothingFoundMessage?: string;
  allowDeselect?: boolean;
  styles?: Record<string, unknown>;
  withAsterisk?: boolean;
  className?: string;
  id?: string;
  maxDropdownHeight?: number;
  comboboxProps?: Record<string, unknown>;
  renderOption?: (opt: { option: { value: string; label: string } }) => ReactNode;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  checkIconPosition?: string;
  filter?: (opts: { options: Array<{ value: string; label: string; disabled?: boolean }>; search: string }) => Array<{ value: string; label: string; disabled?: boolean }>;
  style?: CSSProperties;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onClick?: React.MouseEventHandler<HTMLElement>;
}

function normalizeData(data: SelectData): Array<{ value: string; label: string; disabled?: boolean }> {
  return data.map(d => typeof d === 'string' ? { value: d, label: d } : { value: d.value, label: d.label, disabled: d.disabled });
}

export function Select({
  data, value, onChange, label, placeholder = 'Select...', searchable = false,
  clearable, required, error, description, disabled, mt, mb, mx, my, m, w, flex, miw, maw,
  nothingFoundMessage = 'No results', allowDeselect, withAsterisk, className = '', id,
  maxDropdownHeight, comboboxProps: _cbox, renderOption: _ro, leftSection, rightSection: _rs,
  checkIconPosition: _cip, filter: customFilter,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlighted, setHighlighted] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const options = normalizeData(data);
  const selected = options.find(o => o.value === value);

  const filtered = search && searchable
    ? (customFilter ? customFilter({ options, search }) : options.filter(o => o.label.toLowerCase().includes(search.toLowerCase())))
    : options;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false); setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function select(val: string) {
    if (allowDeselect && val === value) { onChange?.(''); }
    else { onChange?.(val); }
    setOpen(false); setSearch(''); setHighlighted(-1);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, filtered.length - 1)); if (!open) setOpen(true); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && highlighted >= 0 && filtered[highlighted]) { e.preventDefault(); select(filtered[highlighted].value); }
    else if (e.key === 'Escape') { setOpen(false); setSearch(''); }
  }

  const inputId = id || (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  const wrapStyle: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(m ? { margin: sp(m) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(miw ? { minWidth: typeof miw === 'number' ? `${miw}px` : miw } : {}),
    ...(maw ? { maxWidth: typeof maw === 'number' ? `${maw}px` : maw } : {}),
    ...(flex !== undefined ? { flex } : {}),
  };

  return (
    <div className={`form-group ${className}`} ref={containerRef} style={{ position: 'relative', ...wrapStyle }}>
      {label && <label className={`form-label${required || withAsterisk ? ' required' : ''}`} htmlFor={inputId}>{label}</label>}
      {description && <span className="form-help">{description}</span>}
      <div style={{ position: 'relative' }}>
        {leftSection && <span style={{ position: 'absolute', left: '0.5rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-neutral-500)', pointerEvents: 'none', zIndex: 1 }}>{leftSection}</span>}
        {searchable ? (
          <input
            className={`form-input${error ? ' error' : ''}`}
            id={inputId}
            value={open ? search : (selected?.label || '')}
            placeholder={placeholder}
            disabled={disabled}
            onFocus={() => { setOpen(true); setSearch(''); }}
            onChange={e => { setSearch(e.target.value); setHighlighted(0); if (!open) setOpen(true); }}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            style={leftSection ? { paddingLeft: '2rem' } : undefined}
          />
        ) : (
          <button
            type="button"
            className={`form-input${error ? ' error' : ''}`}
            style={{ textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer', background: 'white', paddingLeft: leftSection ? '2rem' : undefined, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            disabled={disabled}
            onClick={() => setOpen(!open)}
            onKeyDown={handleKeyDown}
          >
            {selected?.label || <span style={{ color: 'var(--color-neutral-400)' }}>{placeholder}</span>}
          </button>
        )}
        {clearable && value && !disabled && (
          <button
            type="button"
            onClick={() => { onChange?.(""); setSearch(''); }}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-neutral-400)', fontSize: 16, lineHeight: 1, padding: 4 }}
            aria-label="Clear"
          >&times;</button>
        )}
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          background: 'white', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
          maxHeight: maxDropdownHeight ?? 240, overflowY: 'auto', zIndex: 1000,
          border: '1px solid var(--color-neutral-200)',
        }} role="listbox">
          {filtered.length === 0 ? (
            <div style={{ padding: '0.5rem 0.75rem', color: 'var(--color-neutral-500)', fontSize: 'var(--font-size-sm)', textAlign: 'center' }}>{nothingFoundMessage}</div>
          ) : filtered.map((opt, i) => (
            <div key={opt.value} role="option" aria-selected={opt.value === value}
              style={{
                padding: '0.5rem 0.75rem', cursor: opt.disabled ? 'not-allowed' : 'pointer',
                background: i === highlighted ? 'var(--color-primary-50)' : opt.value === value ? 'var(--color-neutral-50)' : 'white',
                fontWeight: opt.value === value ? 600 : 400, fontSize: 'var(--font-size-sm)', opacity: opt.disabled ? 0.5 : 1,
              }}
              onMouseEnter={() => setHighlighted(i)}
              onClick={() => !opt.disabled && select(opt.value)}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
      {error && <span className="form-error" role="alert">{error}</span>}
    </div>
  );
}

// ── Autocomplete ─────────────────────────────────────────

interface AutocompleteProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange'> {
  data: string[] | Array<{ value: string; label?: string }>;
  value?: string;
  onChange?: (value: string) => void;
  label?: ReactNode;
  error?: ReactNode;
  description?: ReactNode;
  size?: MantineSize;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  w?: MantineSize; flex?: string | number;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  onOptionSubmit?: (value: string) => void;
  limit?: number;
  filter?: (opts: { options: Array<{ value: string; label?: string }>; search: string }) => Array<{ value: string; label?: string }>;
}

export function Autocomplete({
  data, value = '', onChange, label, error, description, mt, mb, mx, my, w, flex,
  leftSection, rightSection, onOptionSubmit, limit = 10, filter,
  size: _size,
  style, className = '', id, ...rest
}: AutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const normalized = data.map(d => typeof d === 'string' ? { value: d, label: d } : { value: d.value, label: d.label ?? d.value });
  const filtered = filter
    ? filter({ options: normalized, search: value })
    : normalized.filter(o => (o.label ?? o.value).toLowerCase().includes(value.toLowerCase())).slice(0, limit);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const inputId = id || (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  const wrapStyle: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(flex !== undefined ? { flex } : {}),
  };

  return (
    <div className={`form-group ${className}`} ref={containerRef} style={{ position: 'relative', ...wrapStyle }}>
      {label && <label className="form-label" htmlFor={inputId}>{label}</label>}
      {description && <span className="form-help">{description}</span>}
      <div style={{ position: 'relative' }}>
        {leftSection && <span style={{ position: 'absolute', left: '0.5rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-neutral-500)', pointerEvents: 'none' }}>{leftSection}</span>}
        <input
          id={inputId}
          className={`form-input${error ? ' error' : ''}`}
          value={value}
          style={{ paddingLeft: leftSection ? '2rem' : undefined, paddingRight: rightSection ? '2rem' : undefined, ...style }}
          onChange={e => { onChange?.(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, filtered.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
            else if (e.key === 'Enter' && highlighted >= 0 && filtered[highlighted]) {
              e.preventDefault();
              const opt = filtered[highlighted];
              onChange?.(opt.label ?? opt.value);
              onOptionSubmit?.(opt.value);
              setOpen(false);
            } else if (e.key === 'Escape') setOpen(false);
          }}
          {...rest}
        />
        {rightSection && <span style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-neutral-500)' }}>{rightSection}</span>}
      </div>
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          background: 'white', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
          maxHeight: 200, overflowY: 'auto', zIndex: 1000, border: '1px solid var(--color-neutral-200)',
        }}>
          {filtered.map((opt, i) => (
            <div key={opt.value}
              style={{
                padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: 'var(--font-size-sm)',
                background: i === highlighted ? 'var(--color-primary-50)' : 'white',
              }}
              onMouseEnter={() => setHighlighted(i)}
              onClick={() => { onChange?.(opt.label ?? opt.value); onOptionSubmit?.(opt.value); setOpen(false); }}
            >
              {opt.label ?? opt.value}
            </div>
          ))}
        </div>
      )}
      {error && <span className="form-error" role="alert">{error}</span>}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────

interface ModalProps {
  opened: boolean;
  onClose: () => void;
  title?: ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | (string & {}) | number;
  children?: ReactNode;
  centered?: boolean;
  closeOnEscape?: boolean;
  closeOnClickOutside?: boolean;
  withCloseButton?: boolean;
  trapFocus?: boolean;
  returnFocus?: boolean;
  overlayProps?: Record<string, unknown>;
  scrollAreaComponent?: React.ElementType;
  transitionProps?: Record<string, unknown>;
  keepMounted?: boolean;
  zIndex?: number;
  styles?: Record<string, unknown>;
  classNames?: Record<string, string>;
  fullScreen?: boolean;
  removeScrollProps?: Record<string, unknown>;
  lockScroll?: boolean;
  padding?: MantineSize | number;
  radius?: MantineSize;
}

export function Modal({
  opened, onClose, title, size = 'md', children, centered = true,
  closeOnEscape = true, closeOnClickOutside = true, withCloseButton = true,
  fullScreen,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  // Stable ref so event handlers never go stale without causing re-runs
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const closeOnEscapeRef = useRef(closeOnEscape);
  closeOnEscapeRef.current = closeOnEscape;

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && closeOnEscapeRef.current) { onCloseRef.current(); return; }
    if (e.key !== 'Tab' || !modalRef.current) return;
    const focusable = modalRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (e.shiftKey) { if (document.activeElement === first) { e.preventDefault(); last.focus(); } }
    else { if (document.activeElement === last) { e.preventDefault(); first.focus(); } }
  }, []); // stable — reads from refs

  // Register/unregister keyboard handler when opened changes
  useEffect(() => {
    if (!opened) return;
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handleKeyDown); document.body.style.overflow = ''; };
  }, [opened, handleKeyDown]);

  // Focus first element only when modal first opens (not on every re-render)
  useEffect(() => {
    if (!opened) return;
    const raf = requestAnimationFrame(() => {
      modalRef.current?.querySelector<HTMLElement>('button, [href], input, select, textarea')?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [opened]); // intentionally omits handleKeyDown so typing doesn't re-trigger focus

  if (!opened) return null;

  const sizeMap: Record<string, string> = {
    xs: 'modal-sm', sm: 'modal-sm', md: '', lg: 'modal-lg', xl: 'modal-xl',
    full: 'modal-full',
  };
  const sizeCls = fullScreen ? 'modal-full' : (typeof size === 'number' ? '' : (sizeMap[size] ?? ''));
  const modalStyle: CSSProperties = typeof size === 'number' ? { maxWidth: size } : {};

  return createPortal(
    <div className="modal-backdrop" style={centered ? {} : { alignItems: 'flex-start', paddingTop: '5vh' }}
      onClick={closeOnClickOutside ? onClose : undefined}>
      <div ref={modalRef} className={`modal ${sizeCls}`} role="dialog" aria-modal="true"
        style={modalStyle} onClick={e => e.stopPropagation()}>
        {(title !== undefined || withCloseButton) && (
          <div className="modal-header">
            {title && <h2 className="modal-title">{title}</h2>}
            {withCloseButton && <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>}
          </div>
        )}
        <div className="modal-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

// ── Tabs ─────────────────────────────────────────────────

interface TabsContextType {
  value: string | null;
  onChange: (val: string) => void;
  orientation?: 'horizontal' | 'vertical';
  variant?: string;
  keepMounted?: boolean;
}

const TabsCtx = React.createContext<TabsContextType>({ value: null, onChange: () => {} });

interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value?: string | null;
  defaultValue?: string;
  onChange?: (value: string) => void;
  orientation?: 'horizontal' | 'vertical';
  variant?: string;
  keepMounted?: boolean;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  m?: MantineSize;
  grow?: boolean;
  styles?: Record<string, unknown>;
  classNames?: Record<string, string>;
  inverted?: boolean;
  allowTabDeactivation?: boolean;
  activateTabWithKeyboard?: boolean;
  placement?: string;
}

function TabsRoot({
  value: valueProp, defaultValue, onChange, orientation = 'horizontal',
  variant, keepMounted, children, mt, mb, mx, my, m,
  style, className = '', ...rest
}: TabsProps) {
  const [internal, setInternal] = useState(defaultValue ?? null);
  const value = valueProp !== undefined ? valueProp : internal;
  const handleChange = useCallback((v: string) => { setInternal(v); onChange?.(v); }, [onChange]);
  const s: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(m ? { margin: sp(m) } : {}),
    ...style,
  };
  return (
    <TabsCtx.Provider value={{ value: value ?? null, onChange: handleChange, orientation, variant, keepMounted }}>
      <div className={className} style={s} {...rest}>{children}</div>
    </TabsCtx.Provider>
  );
}

function TabsList({ children, style, className = '', grow, ...rest }: { children?: ReactNode; style?: CSSProperties; className?: string; grow?: boolean; mt?: MantineSize; mb?: MantineSize }) {
  const { orientation } = React.useContext(TabsCtx);
  return (
    <div
      className={`tabs ${className}`}
      style={{ flexDirection: orientation === 'vertical' ? 'column' : 'row', ...style }}
      role="tablist"
      {...rest}
    >
      {children}
    </div>
  );
}

function TabsTab({
  value, children, leftSection, rightSection, disabled, style, className = '', icon, ...rest
}: {
  value: string; children?: ReactNode; leftSection?: ReactNode; rightSection?: ReactNode;
  disabled?: boolean; style?: CSSProperties; className?: string; icon?: ReactNode;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const { value: active, onChange } = React.useContext(TabsCtx);
  return (
    <button
      className={`tab${active === value ? ' active' : ''} ${className}`}
      role="tab"
      aria-selected={active === value}
      disabled={disabled}
      onClick={() => !disabled && onChange(value)}
      style={style}
      {...rest}
    >
      {icon ?? leftSection}
      {children}
      {rightSection}
    </button>
  );
}

function TabsPanel({ value, children, style, className = '', pt, pb, px, py, p, mt, mb }: {
  value: string; children?: ReactNode; style?: CSSProperties; className?: string;
  pt?: MantineSize; pb?: MantineSize; px?: MantineSize; py?: MantineSize; p?: MantineSize;
  mt?: MantineSize; mb?: MantineSize;
}) {
  const { value: active, keepMounted } = React.useContext(TabsCtx);
  const s: CSSProperties = {
    ...(pt ? { paddingTop: sp(pt) } : {}),
    ...(pb ? { paddingBottom: sp(pb) } : {}),
    ...(px ? { paddingLeft: sp(px), paddingRight: sp(px) } : {}),
    ...(py ? { paddingTop: sp(py), paddingBottom: sp(py) } : {}),
    ...(p ? { padding: sp(p) } : {}),
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...style,
  };
  if (active !== value) return keepMounted ? <div hidden style={s} className={className} /> : null;
  return <div role="tabpanel" style={s} className={className}>{children}</div>;
}

export const Tabs = Object.assign(TabsRoot, { List: TabsList, Tab: TabsTab, Panel: TabsPanel });

// ── Menu ─────────────────────────────────────────────────

interface MenuContextType {
  close: () => void;
}
const MenuCtx = React.createContext<MenuContextType>({ close: () => {} });

interface MenuProps {
  opened?: boolean;
  onChange?: (opened: boolean) => void;
  children?: ReactNode;
  withinPortal?: boolean;
  position?: string;
  offset?: number;
  shadow?: string;
  width?: number | string;
  withArrow?: boolean;
  arrowSize?: number;
  closeOnItemClick?: boolean;
  closeOnClickOutside?: boolean;
  trigger?: string;
  styles?: Record<string, unknown>;
  classNames?: Record<string, string>;
  keepMounted?: boolean;
  transitionProps?: Record<string, unknown>;
  zIndex?: number;
  returnFocus?: boolean;
}

function MenuRoot({ opened: openedProp, onChange, children, closeOnItemClick = true }: MenuProps) {
  const [internal, setInternal] = useState(false);
  const opened = openedProp !== undefined ? openedProp : internal;
  const containerRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => { setInternal(false); onChange?.(false); }, [onChange]);
  const toggle = useCallback(() => {
    const next = !opened;
    setInternal(next);
    onChange?.(next);
  }, [opened, onChange]);

  useEffect(() => {
    if (!opened) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) close();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [opened, close]);

  return (
    <MenuCtx.Provider value={{ close }}>
      <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
        {React.Children.map(children, child => {
          if (!React.isValidElement(child)) return child;
          if ((child.type as any).displayName === 'MenuTarget') {
            return React.cloneElement(child as React.ReactElement<any>, { _toggle: toggle, _opened: opened });
          }
          if ((child.type as any).displayName === 'MenuDropdown') {
            return React.cloneElement(child as React.ReactElement<any>, { _opened: opened, _closeOnItemClick: closeOnItemClick });
          }
          return child;
        })}
      </div>
    </MenuCtx.Provider>
  );
}

function MenuTarget({ children, _toggle, _opened: _o }: { children?: ReactNode; _toggle?: () => void; _opened?: boolean }) {
  return React.cloneElement(children as React.ReactElement<any>, {
    onClick: (e: React.MouseEvent) => {
      _toggle?.();
      (children as React.ReactElement<any>).props?.onClick?.(e);
    }
  });
}
MenuTarget.displayName = 'MenuTarget';

function MenuDropdown({ children, _opened, _closeOnItemClick, style, className = '' }: {
  children?: ReactNode; _opened?: boolean; _closeOnItemClick?: boolean; style?: CSSProperties; className?: string;
}) {
  if (!_opened) return null;
  return (
    <div className={`dropdown-menu active ${className}`} style={{ display: 'block', ...style }}>
      {_closeOnItemClick ? React.Children.map(children, child => {
        if (!React.isValidElement(child)) return child;
        if ((child.type as any).displayName === 'MenuItem') return child;
        return child;
      }) : children}
    </div>
  );
}
MenuDropdown.displayName = 'MenuDropdown';

interface MenuItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  color?: string;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  icon?: ReactNode;
  closeMenuOnClick?: boolean;
  component?: string | React.ElementType;
  href?: string;
  target?: string;
  fw?: number | string;
  fz?: MantineSize;
  c?: string;
}

function MenuItem({ color, leftSection, rightSection, icon, children, onClick, component, href, target, closeMenuOnClick = true, fw, fz, c, ...rest }: MenuItemProps) {
  const { close } = React.useContext(MenuCtx);
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    if (closeMenuOnClick) close();
  };
  const s: CSSProperties = {
    ...(color ? { color: TEXT_COLOR[color] ?? color } : {}),
    ...(c ? { color: TEXT_COLOR[c] ?? c } : {}),
    ...(fw ? { fontWeight: fw } : {}),
    ...(fz ? { fontSize: resolveFontSize(fz) } : {}),
  };
  if (href || component === 'a') {
    return (
      <a href={href} target={target} className="dropdown-item" style={s} onClick={() => { if (closeMenuOnClick) close(); }}>
        {icon ?? leftSection}{children}{rightSection}
      </a>
    );
  }
  return (
    <button type="button" className="dropdown-item" style={s} onClick={handleClick} {...rest}>
      {icon ?? leftSection}{children}{rightSection}
    </button>
  );
}
MenuItem.displayName = 'MenuItem';

function MenuLabel({ children, style, className = '' }: { children?: ReactNode; style?: CSSProperties; className?: string }) {
  return (
    <div className={`dropdown-item ${className}`} style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', fontWeight: 600, cursor: 'default', ...style }}>
      {children}
    </div>
  );
}
function MenuDivider() {
  return <div className="dropdown-divider" />;
}

export const Menu = Object.assign(MenuRoot, {
  Target: MenuTarget, Dropdown: MenuDropdown, Item: MenuItem, Label: MenuLabel, Divider: MenuDivider,
});

// ── Tooltip ─────────────────────────────────────────────

interface TooltipProps {
  label: ReactNode;
  children: ReactNode;
  position?: string;
  withArrow?: boolean;
  multiline?: boolean;
  w?: number | string;
  disabled?: boolean;
  openDelay?: number;
  closeDelay?: number;
  events?: Record<string, boolean>;
  refProp?: string;
  styles?: Record<string, unknown>;
}

export function Tooltip({ label, children, disabled }: TooltipProps) {
  if (disabled) return <>{children}</>;
  return (
    <div className="tooltip-container">
      {children}
      <div className="tooltip" role="tooltip">{label}</div>
    </div>
  );
}

// ── CopyButton ─────────────────────────────────────────────

interface CopyButtonProps {
  value: string;
  timeout?: number;
  children: (props: { copied: boolean; copy: () => void }) => ReactNode;
}

export function CopyButton({ value, timeout = 2000, children }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(() => {
    navigator.clipboard.writeText(value).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), timeout);
  }, [value, timeout]);
  return <>{children({ copied, copy })}</>;
}

// ── Image ─────────────────────────────────────────────────

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src?: string | null;
  alt?: string;
  height?: number | string;
  h?: number | string;
  w?: number | string;
  maw?: number | string;
  mah?: number | string;
  fit?: CSSProperties['objectFit'];
  radius?: MantineSize;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  fallbackSrc?: string;
}

export function Image({ src, alt = '', height, h, w, maw, mah, fit = 'cover', radius, mt, mb, mx, my, fallbackSrc, style, ...rest }: ImageProps) {
  const [src2, setSrc2] = useState(src);
  const effectiveHeight = h ?? height;
  const s: CSSProperties = {
    ...(effectiveHeight ? { height: typeof effectiveHeight === 'number' ? `${effectiveHeight}px` : effectiveHeight } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(maw ? { maxWidth: typeof maw === 'number' ? `${maw}px` : maw } : {}),
    ...(mah ? { maxHeight: typeof mah === 'number' ? `${mah}px` : mah } : {}),
    objectFit: fit,
    ...(radius ? { borderRadius: radius === 'full' ? '50%' : 'var(--radius-md)' } : {}),
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...style,
  };
  return <img src={src2 ?? undefined} alt={alt} style={s} onError={fallbackSrc ? () => setSrc2(fallbackSrc) : undefined} {...rest} />;
}

// ── ScrollArea ─────────────────────────────────────────────

interface ScrollAreaProps extends HTMLAttributes<HTMLDivElement> {
  h?: number | string;
  mah?: number | string;
  w?: number | string;
  maw?: number | string;
  type?: string;
  offsetScrollbars?: boolean;
  scrollbarSize?: number;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize; m?: MantineSize;
  p?: MantineSize; px?: MantineSize; py?: MantineSize; pt?: MantineSize; pb?: MantineSize; pl?: MantineSize; pr?: MantineSize;
  flex?: string | number;
  viewportProps?: Record<string, unknown>;
  onScrollPositionChange?: (pos: { x: number; y: number }) => void;
}

export function ScrollArea({ h, mah, w, maw, mt, mb, mx, my, m, p, px, py, pt, pb, pl, pr, flex, children, style, className = '', ...rest }: ScrollAreaProps) {
  const s: CSSProperties = {
    overflowY: 'auto',
    ...(h ? { height: typeof h === 'number' ? `${h}px` : h } : {}),
    ...(mah ? { maxHeight: typeof mah === 'number' ? `${mah}px` : mah } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(maw ? { maxWidth: typeof maw === 'number' ? `${maw}px` : maw } : {}),
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(m ? { margin: sp(m) } : {}),
    ...(p ? { padding: sp(p) } : {}),
    ...(pt ? { paddingTop: sp(pt) } : {}),
    ...(pb ? { paddingBottom: sp(pb) } : {}),
    ...(pl ? { paddingLeft: sp(pl) } : {}),
    ...(pr ? { paddingRight: sp(pr) } : {}),
    ...(px ? { paddingLeft: sp(px), paddingRight: sp(px) } : {}),
    ...(py ? { paddingTop: sp(py), paddingBottom: sp(py) } : {}),
    ...(flex !== undefined ? { flex } : {}),
    ...style,
  };
  return <div className={className} style={s} {...rest}>{children}</div>;
}

// ── ThemeIcon ─────────────────────────────────────────────

interface ThemeIconProps {
  size?: MantineSize;
  color?: string;
  variant?: string;
  radius?: MantineSize;
  gradient?: { from: string; to: string; deg?: number };
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  mt?: MantineSize; mb?: MantineSize; ml?: MantineSize; mr?: MantineSize;
  mx?: MantineSize; my?: MantineSize;
}

export function ThemeIcon({ size, color, variant, gradient: _gradient, children, style, className = '', mt, mb, ml, mr, mx, my }: ThemeIconProps) {
  const dim = typeof size === 'number' ? size : size === 'xs' ? 20 : size === 'sm' ? 24 : size === 'md' ? 32 : size === 'lg' ? 40 : size === 'xl' ? 48 : 32;
  const bg = color ? (BTN_COLOR[color] ? undefined : color) : 'var(--color-primary-100)';
  const fg = color ? (TEXT_COLOR[color] ?? 'var(--color-primary-600)') : 'var(--color-primary-600)';
  const s: CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: dim, height: dim, borderRadius: 'var(--radius-md)',
    backgroundColor: variant === 'outline' ? 'transparent' : bg,
    color: fg,
    border: variant === 'outline' ? `1px solid ${fg}` : undefined,
    flexShrink: 0,
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(ml ? { marginLeft: sp(ml) } : {}),
    ...(mr ? { marginRight: sp(mr) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...style,
  };
  return <span className={`theme-icon ${className}`} style={s}>{children}</span>;
}

// ── Progress ─────────────────────────────────────────────

interface ProgressProps {
  value: number;
  color?: string;
  size?: MantineSize;
  radius?: MantineSize;
  animated?: boolean;
  striped?: boolean;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  w?: MantineSize;
  style?: CSSProperties;
  className?: string;
  sections?: Array<{ value: number; color?: string; tooltip?: string }>;
  styles?: Record<string, unknown>;
}

function ProgressComponent({ value, color, size, mt, mb, mx, my, w, sections, style, className = '', styles: _styles }: ProgressProps) {
  const h = size === 'xs' ? 4 : size === 'sm' ? 6 : size === 'lg' ? 12 : size === 'xl' ? 16 : typeof size === 'number' ? size : 8;
  const colorMap: Record<string, string> = {
    green: 'var(--color-success)', red: 'var(--color-error)', orange: '#d97706',
    yellow: '#d97706', teal: '#0d9488', blue: 'var(--color-primary-600)',
    gray: 'var(--color-neutral-400)', cyan: '#0e7490', violet: '#7c3aed',
  };
  const s: CSSProperties = {
    height: h,
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...style,
  };
  if (sections) {
    return (
      <div className={`progress ${className}`} style={s}>
        {sections.map((sec, i) => (
          <div key={i} title={sec.tooltip} style={{ width: `${sec.value}%`, height: '100%', backgroundColor: colorMap[sec.color ?? ''] ?? sec.color ?? 'var(--color-primary-600)', display: 'inline-block' }} />
        ))}
      </div>
    );
  }
  return (
    <div className={`progress ${className}`} style={s}>
      <div className="progress-bar" style={{ width: `${Math.min(100, Math.max(0, value))}%`, backgroundColor: colorMap[color ?? ''] ?? color ?? undefined }} />
    </div>
  );
}

// ── RingProgress ─────────────────────────────────────────

interface RingProgressProps {
  sections: Array<{ value: number; color?: string; tooltip?: string }>;
  size?: number;
  thickness?: number;
  label?: ReactNode;
  roundCaps?: boolean;
  style?: CSSProperties;
  className?: string;
}

export function RingProgress({ sections, size = 80, thickness = 12, label, style, className = '' }: RingProgressProps) {
  const r = (size - thickness) / 2;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const colorMap: Record<string, string> = {
    green: 'var(--color-success)', red: 'var(--color-error)', orange: '#d97706',
    yellow: '#d97706', teal: '#0d9488', blue: 'var(--color-primary-600)',
    gray: 'var(--color-neutral-400)', cyan: '#0e7490', violet: '#7c3aed',
    indigo: '#4338ca', pink: '#db2777',
  };
  return (
    <div className={`ring-progress ${className}`} style={{ width: size, height: size, ...style }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-neutral-200)" strokeWidth={thickness} />
        {sections.map((sec, i) => {
          const len = (sec.value / 100) * circ;
          const el = (
            <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none"
              stroke={colorMap[sec.color ?? ''] ?? sec.color ?? 'var(--color-primary-600)'}
              strokeWidth={thickness}
              strokeDasharray={`${len} ${circ - len}`}
              strokeDashoffset={-offset}
            />
          );
          offset += len;
          return el;
        })}
      </svg>
      {label && (
        <div className="ring-progress-label">{label}</div>
      )}
    </div>
  );
}

// ── SegmentedControl ─────────────────────────────────────

type SegmentedData = string[] | Array<{ value: string; label: ReactNode; disabled?: boolean }>;

interface SegmentedControlProps {
  data: SegmentedData;
  value?: string;
  onChange?: (value: string) => void;
  size?: MantineSize;
  color?: string;
  fullWidth?: boolean;
  radius?: MantineSize;
  orientation?: 'horizontal' | 'vertical';
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  w?: MantineSize;
  style?: CSSProperties;
  className?: string;
  disabled?: boolean;
  transitionDuration?: number;
  transitionTimingFunction?: string;
  withItemsBorders?: boolean;
  styles?: Record<string, unknown>;
}

export function SegmentedControl({
  data, value, onChange, fullWidth, mt, mb, mx, my, w, style, className = '', disabled,
}: SegmentedControlProps) {
  const items = data.map(d => typeof d === 'string' ? { value: d, label: d } : d);
  const s: CSSProperties = {
    ...(fullWidth ? { width: '100%', display: 'flex' } : {}),
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...style,
  };
  return (
    <div className={`segmented-control ${className}`} style={s} role="group">
      {items.map(item => (
        <button
          key={item.value}
          type="button"
          className={`segmented-control-item${value === item.value ? ' active' : ''}`}
          style={{ flex: fullWidth ? 1 : undefined }}
          onClick={() => !disabled && !item.disabled && onChange?.(item.value)}
          disabled={disabled || item.disabled}
          aria-pressed={value === item.value}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

// ── Chip ─────────────────────────────────────────────────

interface ChipProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  color?: string;
  size?: MantineSize;
  radius?: MantineSize;
  variant?: string;
  children?: ReactNode;
  value?: string;
  disabled?: boolean;
  style?: CSSProperties;
  className?: string;
}

function ChipComponent({ checked, defaultChecked, onChange, children, disabled, style, className = '' }: ChipProps) {
  const [internal, setInternal] = useState(defaultChecked ?? false);
  const isChecked = checked !== undefined ? checked : internal;
  const toggle = () => {
    if (disabled) return;
    const next = !isChecked;
    setInternal(next);
    onChange?.(next);
  };
  return (
    <button
      type="button"
      className={`chip${isChecked ? ' checked' : ''} ${className}`}
      onClick={toggle}
      disabled={disabled}
      style={style}
      aria-pressed={isChecked}
    >
      {isChecked && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
      {children}
    </button>
  );
}

// ── Table ─────────────────────────────────────────────────

interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  striped?: boolean;
  highlightOnHover?: boolean;
  withBorder?: boolean;
  withTableBorder?: boolean;
  withColumnBorders?: boolean;
  borderColor?: string;
  verticalSpacing?: MantineSize;
  horizontalSpacing?: MantineSize;
  fontSize?: MantineSize;
  fz?: MantineSize;
  captionSide?: 'top' | 'bottom';
  stickyHeader?: boolean;
  stickyHeaderOffset?: number;
  mt?: MantineSize; mb?: MantineSize;
  style?: CSSProperties;
  layout?: string;
}

function TableRoot({ striped, highlightOnHover, withBorder, withTableBorder, fz, children, mt, mb, style, className = '', ...rest }: TableProps) {
  const s: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(fz ? { fontSize: resolveFontSize(fz) } : {}),
    ...style,
  };
  const cls = ['table', striped ? 'table-striped' : '', (withBorder || withTableBorder) ? 'table-bordered' : '', className].filter(Boolean).join(' ');
  return <table className={cls} style={s} {...rest}>{children}</table>;
}

const Thead = ({ children, ...rest }: React.HTMLAttributes<HTMLTableSectionElement>) => <thead {...rest}>{children}</thead>;
const Tbody = ({ children, ...rest }: React.HTMLAttributes<HTMLTableSectionElement>) => <tbody {...rest}>{children}</tbody>;
const Tfoot = ({ children, ...rest }: React.HTMLAttributes<HTMLTableSectionElement>) => <tfoot {...rest}>{children}</tfoot>;
interface TrProps extends React.HTMLAttributes<HTMLTableRowElement> { bg?: string; }
const Tr = ({ children, bg, style, ...rest }: TrProps) => <tr style={{ ...(bg ? { backgroundColor: bg } : {}), ...style }} {...rest}>{children}</tr>;

interface ThProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  ta?: CSSProperties['textAlign'];
  w?: string | number;
  visibleFrom?: string;
  hiddenFrom?: string;
}
const Th = ({ children, ta, w, visibleFrom: _vf, hiddenFrom: _hf, style, ...rest }: ThProps) => (
  <th style={{ textAlign: ta, width: w, ...style }} {...rest}>{children}</th>
);

interface TdProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  ta?: CSSProperties['textAlign'];
  fw?: number | string;
  c?: string;
  fz?: MantineSize;
  w?: string | number;
  maw?: string | number;
  colSpan?: number;
  visibleFrom?: string;
  hiddenFrom?: string;
}
const Td = ({ children, ta, fw, c, fz, w, maw, visibleFrom: _vf, hiddenFrom: _hf, style, ...rest }: TdProps) => (
  <td style={{
    textAlign: ta,
    fontWeight: fw,
    color: c ? (TEXT_COLOR[c] ?? c) : undefined,
    fontSize: fz ? resolveFontSize(fz) : undefined,
    width: w,
    maxWidth: maw !== undefined ? (typeof maw === 'number' ? `${maw}px` : maw) : undefined,
    ...style,
  }} {...rest}>{children}</td>
);

const Caption = ({ children, ...rest }: React.HTMLAttributes<HTMLTableCaptionElement>) => <caption {...rest}>{children}</caption>;

interface TableScrollContainerProps { minWidth?: number; children?: React.ReactNode; style?: CSSProperties; mt?: MantineSize; mb?: MantineSize; }
function TableScrollContainer({ minWidth, children, style, mt, mb }: TableScrollContainerProps) {
  return (
    <div style={{ overflowX: 'auto', ...(mt ? { marginTop: sp(mt) } : {}), ...(mb ? { marginBottom: sp(mb) } : {}), ...style }}>
      <div style={{ minWidth: minWidth ? `${minWidth}px` : undefined }}>
        {children}
      </div>
    </div>
  );
}

export const Table = Object.assign(TableRoot, { Thead, Tbody, Tfoot, Tr, Th, Td, Caption, ScrollContainer: TableScrollContainer });

// ── Box (generic layout primitive) ─────────────────────────

interface BoxProps extends HTMLAttributes<HTMLElement> {
  component?: string | React.ElementType;
  mt?: MantineSize; mb?: MantineSize; ml?: MantineSize; mr?: MantineSize;
  mx?: MantineSize; my?: MantineSize; m?: MantineSize;
  pt?: MantineSize; pb?: MantineSize; pl?: MantineSize; pr?: MantineSize;
  px?: MantineSize; py?: MantineSize; p?: MantineSize;
  w?: MantineSize; h?: MantineSize; maw?: MantineSize; miw?: MantineSize; mah?: MantineSize; mih?: MantineSize;
  display?: string;
  flex?: string | number;
  pos?: CSSProperties['position'];
  top?: MantineSize; right?: MantineSize; bottom?: MantineSize; left?: MantineSize;
  bg?: string; c?: string; bd?: string; opacity?: number | string;
  fz?: MantineSize; fw?: number | string; ta?: CSSProperties['textAlign'];
  lh?: string | number;
  visibleFrom?: string; hiddenFrom?: string;
  bgsz?: string; bgsz_?: string;
  darkHidden?: boolean;
  lightHidden?: boolean;
}

export function Box({
  component, mt, mb, ml, mr, mx, my, m, pt, pb, pl, pr, px, py, p,
  w, h, maw, miw, mah, mih, display, flex, pos, top, right, bottom, left,
  bg, c, bd, opacity, fz, fw, ta, lh,
  visibleFrom: _v, hiddenFrom: _hf, darkHidden: _dh, lightHidden: _lh,
  style, className = '', children, ...rest
}: BoxProps) {
  const Tag = (component as any) ?? 'div';
  const s: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(ml ? { marginLeft: sp(ml) } : {}),
    ...(mr ? { marginRight: sp(mr) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(m ? { margin: sp(m) } : {}),
    ...(pt ? { paddingTop: sp(pt) } : {}),
    ...(pb ? { paddingBottom: sp(pb) } : {}),
    ...(pl ? { paddingLeft: sp(pl) } : {}),
    ...(pr ? { paddingRight: sp(pr) } : {}),
    ...(px ? { paddingLeft: sp(px), paddingRight: sp(px) } : {}),
    ...(py ? { paddingTop: sp(py), paddingBottom: sp(py) } : {}),
    ...(p ? { padding: sp(p) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(h ? { height: typeof h === 'number' ? `${h}px` : h } : {}),
    ...(maw ? { maxWidth: typeof maw === 'number' ? `${maw}px` : maw } : {}),
    ...(miw ? { minWidth: typeof miw === 'number' ? `${miw}px` : miw } : {}),
    ...(mah ? { maxHeight: typeof mah === 'number' ? `${mah}px` : mah } : {}),
    ...(mih ? { minHeight: typeof mih === 'number' ? `${mih}px` : mih } : {}),
    ...(display ? { display } : {}),
    ...(flex !== undefined ? { flex } : {}),
    ...(pos ? { position: pos } : {}),
    ...(top !== undefined ? { top: sp(top) } : {}),
    ...(right !== undefined ? { right: sp(right) } : {}),
    ...(bottom !== undefined ? { bottom: sp(bottom) } : {}),
    ...(left !== undefined ? { left: sp(left) } : {}),
    ...(bg ? { backgroundColor: bg } : {}),
    ...(c ? { color: TEXT_COLOR[c] ?? c } : {}),
    ...(bd ? { border: bd } : {}),
    ...(opacity !== undefined ? { opacity: Number(opacity) } : {}),
    ...(fz ? { fontSize: resolveFontSize(fz) } : {}),
    ...(fw ? { fontWeight: fw } : {}),
    ...(ta ? { textAlign: ta } : {}),
    ...(lh ? { lineHeight: lh } : {}),
    ...style,
  };
  return <Tag className={className} style={s} {...rest}>{children}</Tag>;
}

// ── NumberInput ───────────────────────────────────────────

interface NumberInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange' | 'value'> {
  value?: number | string;
  onChange?: (value: number | string) => void;
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  size?: MantineSize;
  min?: number;
  max?: number;
  step?: number;
  decimalScale?: number;
  fixedDecimalScale?: boolean;
  thousandSeparator?: string | boolean;
  prefix?: string;
  suffix?: string;
  allowNegative?: boolean;
  allowDecimal?: boolean;
  clampBehavior?: string;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  m?: MantineSize; w?: MantineSize; flex?: string | number; miw?: MantineSize;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  hideControls?: boolean;
  withAsterisk?: boolean;
  styles?: Record<string, unknown>;
}

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  function NumberInput({
    label, description, error, required, value, onChange, withAsterisk,
    min, max, step, mt, mb, mx, my, m, w, flex, miw,
    leftSection, rightSection, hideControls: _hc,
    size: _size, decimalScale: _ds, fixedDecimalScale: _fds,
    thousandSeparator: _ts, prefix: _prefix, suffix: _suffix,
    allowNegative: _an, allowDecimal: _ad, clampBehavior: _cb,
    styles: _styles,
    style, className = '', id, ...rest
  }, ref) {
    const wrapStyle: CSSProperties = {
      ...(mt ? { marginTop: sp(mt) } : {}),
      ...(mb ? { marginBottom: sp(mb) } : {}),
      ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
      ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
      ...(m ? { margin: sp(m) } : {}),
      ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
      ...(miw ? { minWidth: typeof miw === 'number' ? `${miw}px` : miw } : {}),
      ...(flex !== undefined ? { flex } : {}),
    };
    const inputId = id || (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
    return (
      <div className={`form-group ${className}`} style={wrapStyle}>
        {label && (
          <label className={`form-label${required || withAsterisk ? ' required' : ''}`} htmlFor={inputId}>
            {label}
          </label>
        )}
        {description && <span className="form-help">{description}</span>}
        <div style={{ position: 'relative' }}>
          {leftSection && <span style={{ position: 'absolute', left: '0.5rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-neutral-500)', pointerEvents: 'none' }}>{leftSection}</span>}
          <input
            ref={ref}
            type="number"
            id={inputId}
            className={`form-input${error ? ' error' : ''}`}
            value={value ?? ''}
            min={min}
            max={max}
            step={step}
            style={{ paddingLeft: leftSection ? '2rem' : undefined, paddingRight: rightSection ? '2rem' : undefined, ...style }}
            onChange={e => {
              const v = e.target.value;
              onChange?.(v === '' ? '' : Number(v));
            }}
            {...rest}
          />
          {rightSection && <span style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-neutral-500)' }}>{rightSection}</span>}
        </div>
        {error && <span className="form-error" role="alert">{error}</span>}
      </div>
    );
  }
);

// ── Checkbox ──────────────────────────────────────────────

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  size?: MantineSize;
  color?: string;
  radius?: MantineSize;
  indeterminate?: boolean;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  style?: CSSProperties;
  styles?: Record<string, unknown>;
  labelPosition?: 'left' | 'right';
  wrapperProps?: Record<string, unknown>;
  icon?: React.FC<{ indeterminate: boolean; className: string }>;
}

const CheckboxBase = React.forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox({ label, description, error, indeterminate, mt, mb, mx, my, style, className = '', id,
    size: _size, color: _color, radius: _radius, styles: _styles,
    labelPosition: _lp, wrapperProps: _wp, icon: _icon,
    ...rest }, ref) {
    const inputId = id || (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
    const wrapStyle: CSSProperties = {
      ...(mt ? { marginTop: sp(mt) } : {}),
      ...(mb ? { marginBottom: sp(mb) } : {}),
      ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
      ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
      ...style,
    };
    return (
      <div className={`form-check ${className}`} style={wrapStyle}>
        <input
          ref={el => {
            if (ref) { if (typeof ref === 'function') ref(el); else (ref as any).current = el; }
            if (el) el.indeterminate = indeterminate ?? false;
          }}
          type="checkbox"
          id={inputId}
          {...rest}
        />
        {label && <label htmlFor={inputId}><span>{label}</span></label>}
        {description && <span className="form-help">{description}</span>}
        {error && <span className="form-error" role="alert">{error}</span>}
      </div>
    );
  }
);

// ── Skeleton ──────────────────────────────────────────────

interface SkeletonProps {
  visible?: boolean;
  animate?: boolean;
  height?: number | string;
  width?: number | string;
  radius?: MantineSize;
  circle?: boolean;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  style?: CSSProperties;
  className?: string;
  children?: ReactNode;
}

export function Skeleton({ visible = true, height, width, circle, mt, mb, mx, my, style, className = '', children }: SkeletonProps) {
  if (!visible) return <>{children}</>;
  const s: CSSProperties = {
    height: height ? (typeof height === 'number' ? `${height}px` : height) : '1rem',
    width: width ? (typeof width === 'number' ? `${width}px` : width) : '100%',
    borderRadius: circle ? '50%' : 'var(--radius-md)',
    backgroundColor: 'var(--color-neutral-200)',
    animation: 'skeleton-pulse 1.5s ease-in-out infinite',
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...style,
  };
  return <div className={className} style={s} aria-hidden="true" />;
}

// ── Stepper ────────────────────────────────────────────────

interface StepperContextType {
  active: number;
  onStepClick?: (step: number) => void;
}
const StepperCtx = React.createContext<StepperContextType>({ active: 0 });

interface StepperProps {
  active: number;
  onStepClick?: (step: number) => void;
  children?: ReactNode;
  color?: string;
  size?: MantineSize;
  orientation?: 'horizontal' | 'vertical';
  completedIcon?: ReactNode;
  mt?: MantineSize; mb?: MantineSize;
  style?: CSSProperties;
  className?: string;
  allowNextStepsSelect?: boolean;
  styles?: Record<string, unknown>;
}

function StepperRoot({ active, onStepClick, children, mt, mb, style, className = '' }: StepperProps) {
  const steps = React.Children.toArray(children).filter(c => React.isValidElement(c) && (c.type as any).displayName === 'StepperStep');
  const completed = React.Children.toArray(children).find(c => React.isValidElement(c) && (c.type as any).displayName === 'StepperCompleted');
  const s: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...style,
  };
  return (
    <StepperCtx.Provider value={{ active, onStepClick }}>
      <div className={className} style={s}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1.5rem' }}>
          {steps.map((step, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div style={{ flex: 1, height: 2, backgroundColor: i <= active ? 'var(--color-primary-600)' : 'var(--color-neutral-200)', borderRadius: 1 }} />}
              {React.cloneElement(step as React.ReactElement<any>, { _index: i, _active: active })}
            </React.Fragment>
          ))}
        </div>
        {active >= steps.length && completed ? (
          <div>{(completed as React.ReactElement<any>).props.children}</div>
        ) : (
          steps[active] && <div>{(steps[active] as React.ReactElement<any>).props.children}</div>
        )}
      </div>
    </StepperCtx.Provider>
  );
}

function StepperStep({ label, description, icon, _index = 0, _active = 0, children: _c, completedIcon, ...rest }: {
  label?: ReactNode; description?: ReactNode; icon?: ReactNode; _index?: number; _active?: number;
  children?: ReactNode; completedIcon?: ReactNode; [key: string]: unknown;
}) {
  const { onStepClick } = React.useContext(StepperCtx);
  const isDone = _index < _active;
  const isCurrent = _index === _active;
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', cursor: onStepClick ? 'pointer' : 'default', minWidth: 60 }}
      onClick={() => onStepClick?.(_index)}
      {...(rest as any)}
    >
      <div style={{
        width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: isDone ? 'var(--color-success)' : isCurrent ? 'var(--color-primary-600)' : 'var(--color-neutral-200)',
        color: isDone || isCurrent ? 'white' : 'var(--color-neutral-600)',
        fontSize: 'var(--font-size-sm)', fontWeight: 600,
      }}>
        {isDone ? (completedIcon ?? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>) : (icon ?? (_index + 1))}
      </div>
      {label && <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: isCurrent ? 600 : 400, textAlign: 'center', color: isCurrent ? 'var(--color-neutral-900)' : 'var(--color-neutral-600)' }}>{label}</span>}
      {description && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', textAlign: 'center' }}>{description}</span>}
    </div>
  );
}
StepperStep.displayName = 'StepperStep';

function StepperCompleted({ children }: { children?: ReactNode }) {
  return <>{children}</>;
}
StepperCompleted.displayName = 'StepperCompleted';

export const Stepper = Object.assign(StepperRoot, { Step: StepperStep, Completed: StepperCompleted });

// ── Anchor ────────────────────────────────────────────────

interface AnchorProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  component?: string | React.ElementType;
  underline?: 'always' | 'hover' | 'never';
  c?: string;
  fw?: number | string;
  fz?: MantineSize;
  size?: MantineSize;
  mt?: MantineSize; mb?: MantineSize;
}

export function Anchor({ c, fw, fz, size: _size, underline = 'hover', mt, mb, style, children, ...rest }: AnchorProps) {
  const s: CSSProperties = {
    color: c ? (TEXT_COLOR[c] ?? c) : 'var(--color-primary-600)',
    textDecoration: underline === 'always' ? 'underline' : underline === 'never' ? 'none' : undefined,
    cursor: 'pointer',
    ...(fw ? { fontWeight: fw } : {}),
    ...(fz ? { fontSize: resolveFontSize(fz) } : {}),
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...style,
  };
  return <a style={s} {...rest}>{children}</a>;
}

// ── Code ──────────────────────────────────────────────────

interface CodeProps {
  children?: ReactNode;
  color?: string;
  block?: boolean;
  style?: CSSProperties;
  className?: string;
  mt?: MantineSize; mb?: MantineSize;
}

export function Code({ children, block, style, className = '', mt, mb }: CodeProps) {
  const s: CSSProperties = {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.875em',
    backgroundColor: 'var(--color-neutral-100)',
    padding: block ? '0.75rem 1rem' : '0.1rem 0.35rem',
    borderRadius: 'var(--radius-sm)',
    display: block ? 'block' : 'inline',
    whiteSpace: block ? 'pre-wrap' : undefined,
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...style,
  };
  return block
    ? <pre className={className} style={s}><code>{children}</code></pre>
    : <code className={className} style={s}>{children}</code>;
}

// ── Pagination ────────────────────────────────────────────

interface PaginationProps {
  total: number;
  value?: number;
  onChange?: (page: any) => void;
  defaultValue?: number;
  siblings?: number;
  boundaries?: number;
  size?: MantineSize;
  mt?: MantineSize; mb?: MantineSize;
  style?: CSSProperties;
  className?: string;
  disabled?: boolean;
  withEdges?: boolean;
  withControls?: boolean;
}

export function Pagination({ total, value, onChange, defaultValue = 1, size: _size, mt, mb, style, className = '' }: PaginationProps) {
  const [internal, setInternal] = useState(defaultValue);
  const page = value !== undefined ? value : internal;
  const set = (p: number) => { setInternal(p); onChange?.(p); };
  const s: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...style,
  };
  const pages: number[] = [];
  for (let i = 1; i <= total; i++) pages.push(i);
  return (
    <nav aria-label="Pagination" style={s} className={className}>
      <ul className="pagination">
        <li><button className={`page-link${page === 1 ? ' disabled' : ''}`} onClick={() => page > 1 && set(page - 1)} aria-label="Previous">&lsaquo;</button></li>
        {pages.map(p => (
          <li key={p}><button className={`page-link${p === page ? ' active' : ''}`} onClick={() => set(p)} aria-label={`Page ${p}`} aria-current={p === page ? 'page' : undefined}>{p}</button></li>
        ))}
        <li><button className={`page-link${page === total ? ' disabled' : ''}`} onClick={() => page < total && set(page + 1)} aria-label="Next">&rsaquo;</button></li>
      </ul>
    </nav>
  );
}

// ── Switch ────────────────────────────────────────────────

interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  size?: MantineSize;
  color?: string;
  thumbIcon?: ReactNode;
  onLabel?: ReactNode;
  offLabel?: ReactNode;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  style?: CSSProperties;
  styles?: Record<string, unknown>;
  labelPosition?: 'left' | 'right';
  wrapperProps?: Record<string, unknown>;
}

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  function Switch({ label, description, error, mt, mb, mx, my, style, className = '', id,
    size: _size, color: _color, thumbIcon: _ti, onLabel: _ol, offLabel: _off,
    styles: _styles, labelPosition: _lp, wrapperProps: _wp,
    ...rest }, ref) {
    const inputId = id || (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
    const wrapStyle: CSSProperties = {
      ...(mt ? { marginTop: sp(mt) } : {}),
      ...(mb ? { marginBottom: sp(mb) } : {}),
      ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
      ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
      ...style,
    };
    return (
      <label className={`form-check ${className}`} style={{ alignItems: 'flex-start', ...wrapStyle }} htmlFor={inputId}>
        <input ref={ref} type="checkbox" role="switch" id={inputId} style={{ accentColor: 'var(--color-primary-600)' }} {...rest} />
        {(label || description) && (
          <span>
            {label && <span style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>{label}</span>}
            {description && <span className="form-help" style={{ marginTop: '0.1rem' }}>{description}</span>}
          </span>
        )}
        {error && <span className="form-error" role="alert">{error}</span>}
      </label>
    );
  }
);

// ── Radio ─────────────────────────────────────────────────

interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  size?: MantineSize;
  color?: string;
  mt?: MantineSize; mb?: MantineSize;
  style?: CSSProperties;
  styles?: Record<string, unknown>;
  wrapperProps?: Record<string, unknown>;
}

interface RadioGroupProps {
  value?: string;
  onChange?: (value: string) => void;
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  name?: string;
  children?: ReactNode;
  mt?: MantineSize; mb?: MantineSize;
  style?: CSSProperties;
  withAsterisk?: boolean;
}

const RadioRoot = React.forwardRef<HTMLInputElement, RadioProps>(
  function Radio({ label, description, error, mt, mb, style, className = '', id,
    size: _size, color: _color, styles: _styles, wrapperProps: _wp,
    ...rest }, ref) {
    const inputId = id || (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
    const wrapStyle: CSSProperties = {
      ...(mt ? { marginTop: sp(mt) } : {}),
      ...(mb ? { marginBottom: sp(mb) } : {}),
      ...style,
    };
    return (
      <label className={`form-check ${className}`} style={wrapStyle} htmlFor={inputId}>
        <input ref={ref} type="radio" id={inputId} {...rest} />
        {label && <span>{label}</span>}
        {description && <span className="form-help">{description}</span>}
        {error && <span className="form-error" role="alert">{error}</span>}
      </label>
    );
  }
);

function RadioGroup({ label, description, error, required, children, mt, mb, style, withAsterisk, name }: RadioGroupProps) {
  const wrapStyle: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...style,
  };
  return (
    <div role="group" style={wrapStyle}>
      {label && <div className={`form-label${required || withAsterisk ? ' required' : ''}`}>{label}</div>}
      {description && <span className="form-help">{description}</span>}
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) return child;
        return React.cloneElement(child as React.ReactElement<any>, { name });
      })}
      {error && <span className="form-error" role="alert">{error}</span>}
    </div>
  );
}

export const Radio = Object.assign(RadioRoot, { Group: RadioGroup });

// ── Avatar ────────────────────────────────────────────────

interface AvatarProps {
  src?: string | null;
  alt?: string;
  size?: MantineSize | number;
  radius?: MantineSize;
  color?: string;
  variant?: string;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  mt?: MantineSize; mb?: MantineSize;
  component?: string | React.ElementType;
  href?: string;
}

export function Avatar({ src, alt = '', size = 'md', radius = 'full', color, children, style, className = '', mt, mb, component, href }: AvatarProps) {
  const dim = typeof size === 'number' ? size : size === 'xs' ? 24 : size === 'sm' ? 32 : size === 'md' ? 40 : size === 'lg' ? 56 : size === 'xl' ? 72 : 40;
  const s: CSSProperties = {
    width: dim, height: dim,
    borderRadius: radius === 'full' ? '50%' : 'var(--radius-md)',
    overflow: 'hidden', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: src ? 'transparent' : (color ? undefined : 'var(--color-neutral-300)'),
    flexShrink: 0,
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...style,
  };
  const Tag = (component as any) ?? (href ? 'a' : 'div');
  return (
    <Tag className={className} style={s} href={href} aria-label={alt}>
      {src ? <img src={src} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : children}
    </Tag>
  );
}

// ── FileButton ────────────────────────────────────────────

interface FileButtonProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (file: any) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  capture?: boolean | string;
  inputProps?: Record<string, unknown>;
  resetRef?: React.RefObject<() => void>;
  children: (props: { onClick: () => void }) => ReactNode;
}

export function FileButton({ onChange, accept, multiple, disabled, resetRef, children }: FileButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (resetRef) {
      (resetRef as React.MutableRefObject<() => void>).current = () => {
        if (inputRef.current) inputRef.current.value = '';
      };
    }
  }, [resetRef]);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        style={{ display: 'none' }}
        onChange={e => {
          const files = e.target.files;
          onChange(files && files.length > 0 ? files[0] : null);
        }}
      />
      {children({ onClick: () => !disabled && inputRef.current?.click() })}
    </>
  );
}

// ── Collapse ──────────────────────────────────────────────

interface CollapseProps {
  in: boolean;
  children?: ReactNode;
  transitionDuration?: number;
  transitionTimingFunction?: string;
  onTransitionEnd?: () => void;
  animateOpacity?: boolean;
}

export function Collapse({ in: opened, children }: CollapseProps) {
  if (!opened) return null;
  return <>{children}</>;
}

// ── MultiSelect ───────────────────────────────────────────

interface MultiSelectProps {
  data: SelectData;
  value?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (value: any) => void;
  label?: ReactNode;
  placeholder?: string;
  searchable?: boolean;
  clearable?: boolean;
  required?: boolean;
  error?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  size?: MantineSize;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  m?: MantineSize; w?: MantineSize; flex?: string | number; miw?: MantineSize;
  maxValues?: number;
  maxDropdownHeight?: number;
  nothingFoundMessage?: string;
  withAsterisk?: boolean;
  className?: string;
  id?: string;
  styles?: Record<string, unknown>;
  checkIconPosition?: string;
  hidePickedOptions?: boolean;
  comboboxProps?: Record<string, unknown>;
  leftSection?: ReactNode;
}

export function MultiSelect({
  data, value = [], onChange, label, placeholder = 'Select...', searchable = true,
  clearable, required, error, description, disabled, mt, mb, mx, my, m, w, flex, miw,
  maxValues, nothingFoundMessage = 'No results', withAsterisk, className = '', id, hidePickedOptions,
  leftSection,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const options = normalizeData(data);
  const filtered = search && searchable
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;
  const visibleOptions = hidePickedOptions ? filtered.filter(o => !value.includes(o.value)) : filtered;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false); setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function toggle(val: string) {
    if (value.includes(val)) {
      onChange?.(value.filter(v => v !== val));
    } else {
      if (maxValues && value.length >= maxValues) return;
      onChange?.([...value, val]);
    }
    setSearch('');
  }

  const inputId = id || (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  const wrapStyle: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(m ? { margin: sp(m) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(miw ? { minWidth: typeof miw === 'number' ? `${miw}px` : miw } : {}),
    ...(flex !== undefined ? { flex } : {}),
  };

  const selectedLabels = value.map(v => options.find(o => o.value === v)?.label ?? v);

  return (
    <div className={`form-group ${className}`} ref={containerRef} style={{ position: 'relative', ...wrapStyle }}>
      {label && <label className={`form-label${required || withAsterisk ? ' required' : ''}`} htmlFor={inputId}>{label}</label>}
      {description && <span className="form-help">{description}</span>}
      <div
        className={`form-input${error ? ' error' : ''}`}
        style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', minHeight: '2.25rem', height: 'auto', cursor: disabled ? 'not-allowed' : 'pointer', paddingTop: '0.25rem', paddingBottom: '0.25rem', alignItems: 'center', paddingLeft: leftSection ? '2rem' : undefined }}
        onClick={() => !disabled && setOpen(true)}
      >
        {leftSection && <span style={{ flexShrink: 0, color: 'var(--color-neutral-500)' }}>{leftSection}</span>}
        {selectedLabels.map((lbl, i) => (
          <span key={value[i]} className="badge badge-blue" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
            {lbl}
            {!disabled && (
              <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: 'inherit' }}
                onClick={e => { e.stopPropagation(); toggle(value[i]); }}>&times;</button>
            )}
          </span>
        ))}
        {searchable && open ? (
          <input
            autoFocus
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ border: 'none', outline: 'none', flexGrow: 1, minWidth: 60, fontSize: 'var(--font-size-sm)' }}
            placeholder={value.length === 0 ? placeholder : ''}
          />
        ) : value.length === 0 && (
          <span style={{ color: 'var(--color-neutral-400)', fontSize: 'var(--font-size-sm)' }}>{placeholder}</span>
        )}
        {clearable && value.length > 0 && !disabled && (
          <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-neutral-400)', marginLeft: 'auto', fontSize: 16 }}
            onClick={e => { e.stopPropagation(); onChange?.([]); }}>&times;</button>
        )}
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          background: 'white', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
          maxHeight: 240, overflowY: 'auto', zIndex: 1000, border: '1px solid var(--color-neutral-200)',
        }} role="listbox">
          {visibleOptions.length === 0 ? (
            <div style={{ padding: '0.5rem 0.75rem', color: 'var(--color-neutral-500)', fontSize: 'var(--font-size-sm)', textAlign: 'center' }}>{nothingFoundMessage}</div>
          ) : visibleOptions.map(opt => (
            <div key={opt.value} role="option" aria-selected={value.includes(opt.value)}
              style={{
                padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: 'var(--font-size-sm)',
                background: value.includes(opt.value) ? 'var(--color-primary-50)' : 'white',
                fontWeight: value.includes(opt.value) ? 600 : 400,
                display: 'flex', alignItems: 'center', gap: '0.5rem',
              }}
              onClick={() => !opt.disabled && toggle(opt.value)}
            >
              <input type="checkbox" readOnly checked={value.includes(opt.value)} style={{ accentColor: 'var(--color-primary-600)' }} />
              {opt.label}
            </div>
          ))}
        </div>
      )}
      {error && <span className="form-error" role="alert">{error}</span>}
    </div>
  );
}

// ── Popover ───────────────────────────────────────────────

interface PopoverContextType { close: () => void; opened: boolean; }
const PopoverCtx = React.createContext<PopoverContextType>({ close: () => {}, opened: false });

interface PopoverProps {
  opened?: boolean;
  onChange?: (opened: boolean) => void;
  onClose?: () => void;
  onOpen?: () => void;
  children?: ReactNode;
  position?: string;
  offset?: number;
  withArrow?: boolean;
  arrowSize?: number;
  shadow?: string;
  width?: number | string;
  withinPortal?: boolean;
  trapFocus?: boolean;
  closeOnEscape?: boolean;
  closeOnClickOutside?: boolean;
  returnFocus?: boolean;
  zIndex?: number;
  styles?: Record<string, unknown>;
  classNames?: Record<string, string>;
  transitionProps?: Record<string, unknown>;
  middlewares?: Record<string, unknown>;
  radius?: MantineSize;
  keepMounted?: boolean;
  defaultOpened?: boolean;
  disabled?: boolean;
}

function PopoverRoot({ opened: openedProp, onChange, children, closeOnClickOutside = true, closeOnEscape = true }: PopoverProps) {
  const [internal, setInternal] = useState(false);
  const opened = openedProp !== undefined ? openedProp : internal;
  const containerRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => { setInternal(false); onChange?.(false); }, [onChange]);
  const toggle = useCallback(() => { const next = !opened; setInternal(next); onChange?.(next); }, [opened, onChange]);

  useEffect(() => {
    if (!opened || !closeOnClickOutside) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) close();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [opened, closeOnClickOutside, close]);

  useEffect(() => {
    if (!opened || !closeOnEscape) return;
    function handleKey(e: KeyboardEvent) { if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [opened, closeOnEscape, close]);

  return (
    <PopoverCtx.Provider value={{ close, opened }}>
      <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
        {React.Children.map(children, child => {
          if (!React.isValidElement(child)) return child;
          if ((child.type as any).displayName === 'PopoverTarget') return React.cloneElement(child as React.ReactElement<any>, { _toggle: toggle });
          if ((child.type as any).displayName === 'PopoverDropdown') return React.cloneElement(child as React.ReactElement<any>, { _opened: opened });
          return child;
        })}
      </div>
    </PopoverCtx.Provider>
  );
}

function PopoverTarget({ children, _toggle }: { children?: ReactNode; _toggle?: () => void }) {
  return React.cloneElement(children as React.ReactElement<any>, {
    onClick: (e: React.MouseEvent) => { _toggle?.(); (children as React.ReactElement<any>).props?.onClick?.(e); }
  });
}
PopoverTarget.displayName = 'PopoverTarget';

function PopoverDropdown({ children, _opened, style, className = '', p, py, px }: { children?: ReactNode; _opened?: boolean; style?: CSSProperties; className?: string; p?: MantineSize | number; py?: MantineSize; px?: MantineSize; }) {
  if (!_opened) return null;
  const pVal = p !== undefined ? (typeof p === 'number' ? `${p * 0.25}rem` : sp(p)) : '0.5rem';
  return (
    <div className={className} style={{
      position: 'absolute', top: '100%', left: 0, marginTop: 4,
      background: 'white', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
      border: '1px solid var(--color-neutral-200)', zIndex: 500, padding: pVal,
      ...(py ? { paddingTop: sp(py), paddingBottom: sp(py) } : {}),
      ...(px ? { paddingLeft: sp(px), paddingRight: sp(px) } : {}),
      minWidth: 200, ...style,
    }}>
      {children}
    </div>
  );
}
PopoverDropdown.displayName = 'PopoverDropdown';

export const Popover = Object.assign(PopoverRoot, { Target: PopoverTarget, Dropdown: PopoverDropdown });

// ── Drawer ────────────────────────────────────────────────

interface DrawerProps {
  opened: boolean;
  onClose: () => void;
  title?: ReactNode;
  size?: string | number;
  position?: 'left' | 'right' | 'top' | 'bottom';
  children?: ReactNode;
  withCloseButton?: boolean;
  closeOnEscape?: boolean;
  closeOnClickOutside?: boolean;
  overlayProps?: Record<string, unknown>;
  transitionProps?: Record<string, unknown>;
  zIndex?: number;
  keepMounted?: boolean;
  padding?: MantineSize;
  scrollAreaComponent?: React.ElementType;
  styles?: Record<string, unknown>;
  classNames?: Record<string, string>;
  withOverlay?: boolean;
  lockScroll?: boolean;
  returnFocus?: boolean;
}

export function Drawer({ opened, onClose, title, size = 'md', position = 'right', children, withCloseButton = true, closeOnEscape = true, closeOnClickOutside = true }: DrawerProps) {
  useEffect(() => {
    if (!opened || !closeOnEscape) return;
    function handleKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [opened, closeOnEscape, onClose]);

  if (!opened) return null;
  const sizeMap: Record<string, string> = { xs: '280px', sm: '350px', md: '400px', lg: '500px', xl: '600px' };
  const w = typeof size === 'number' ? `${size}px` : (sizeMap[size] ?? size);
  const drawerStyle: CSSProperties = {
    position: 'fixed', top: 0, bottom: 0, zIndex: 900,
    background: 'white', boxShadow: 'var(--shadow-xl)',
    display: 'flex', flexDirection: 'column',
    ...(position === 'right' ? { right: 0, width: w } : position === 'left' ? { left: 0, width: w } : {}),
  };
  return createPortal(
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 899 }} onClick={closeOnClickOutside ? onClose : undefined} />
      <div style={drawerStyle} role="dialog" aria-modal="true">
        {(title !== undefined || withCloseButton) && (
          <div className="modal-header">
            {title && <h2 className="modal-title">{title}</h2>}
            {withCloseButton && <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>}
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>{children}</div>
      </div>
    </>,
    document.body,
  );
}

// ── Slider ────────────────────────────────────────────────

interface SliderProps {
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  onChangeEnd?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: ReactNode | ((value: number) => ReactNode) | null;
  marks?: Array<{ value: number; label?: ReactNode }>;
  color?: string;
  size?: MantineSize;
  radius?: MantineSize;
  disabled?: boolean;
  inverted?: boolean;
  thumbLabel?: string;
  thumbSize?: number;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  w?: MantineSize;
  style?: CSSProperties;
  className?: string;
  labelAlwaysOn?: boolean;
  showLabelOnHover?: boolean;
  styles?: Record<string, unknown>;
  precision?: number;
  restrictToMarks?: boolean;
}

export function Slider({
  value: valueProp, defaultValue = 0, onChange, onChangeEnd,
  min = 0, max = 100, step = 1, disabled, marks,
  mt, mb, mx, my, w, style, className = '',
}: SliderProps) {
  const [internal, setInternal] = useState(defaultValue);
  const value = valueProp !== undefined ? valueProp : internal;
  const s: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...style,
  };
  return (
    <div className={className} style={s}>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        disabled={disabled}
        style={{ width: '100%', accentColor: 'var(--color-primary-600)' }}
        onChange={e => { const v = Number(e.target.value); setInternal(v); onChange?.(v); }}
        onMouseUp={e => onChangeEnd?.(Number((e.target as HTMLInputElement).value))}
      />
      {marks && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
          {marks.map(m => (
            <div key={m.value} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.1rem' }}>
              <div style={{ width: 2, height: 6, background: 'var(--color-neutral-400)' }} />
              {m.label && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)' }}>{m.label}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── FileInput ─────────────────────────────────────────────

interface FileInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'onChange' | 'value'> {
  value?: File | File[] | null;
  onChange?: (file: File | null) => void;
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  size?: MantineSize;
  clearable?: boolean;
  multiple?: boolean;
  accept?: string;
  placeholder?: string;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  w?: MantineSize; flex?: string | number;
  withAsterisk?: boolean;
  styles?: Record<string, unknown>;
  valueComponent?: React.ElementType;
}

export function FileInput({
  label, description, error, required, multiple, accept, placeholder = 'Pick file',
  clearable, onChange, value: _v, leftSection, withAsterisk,
  mt, mb, mx, my, w, flex, style, className = '', id,
  size: _size, rightSection: _rs, styles: _styles, valueComponent: _vc,
  ...rest
}: FileInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string>('');
  const inputId = id || (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  const wrapStyle: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(flex !== undefined ? { flex } : {}),
  };
  return (
    <div className={`form-group ${className}`} style={wrapStyle}>
      {label && <label className={`form-label${required || withAsterisk ? ' required' : ''}`} htmlFor={inputId}>{label}</label>}
      {description && <span className="form-help">{description}</span>}
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="file"
          id={inputId}
          accept={accept}
          multiple={multiple}
          style={{ display: 'none' }}
          onChange={e => {
            const files = e.target.files;
            const file = files && files.length > 0 ? files[0] : null;
            setFileName(file ? file.name : '');
            onChange?.(file);
          }}
          {...rest}
        />
        <div
          className={`form-input${error ? ' error' : ''}`}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', ...style }}
          onClick={() => inputRef.current?.click()}
        >
          {leftSection}
          <span style={{ flex: 1, color: fileName ? 'inherit' : 'var(--color-neutral-400)' }}>{fileName || placeholder}</span>
          {clearable && fileName && (
            <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-neutral-400)', fontSize: 16 }}
              onClick={e => { e.stopPropagation(); setFileName(''); onChange?.(null); if (inputRef.current) inputRef.current.value = ''; }}>&times;</button>
          )}
        </div>
      </div>
      {error && <span className="form-error" role="alert">{error}</span>}
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  p?: MantineSize;
  pt?: MantineSize; pb?: MantineSize; px?: MantineSize; py?: MantineSize;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize; m?: MantineSize;
  withBorder?: boolean;
  shadow?: string;
  radius?: MantineSize;
  bg?: string; c?: string; bd?: string;
  w?: MantineSize; h?: MantineSize; maw?: MantineSize; miw?: MantineSize; mah?: MantineSize;
  pos?: CSSProperties['position'];
  top?: MantineSize; right?: MantineSize; bottom?: MantineSize; left?: MantineSize;
  display?: string; flex?: string | number;
  ta?: CSSProperties['textAlign'];
  fz?: MantineSize; fw?: number | string;
  component?: string | React.ElementType;
  padding?: MantineSize;
}

function CardRoot({
  p = 'md', pt, pb, px, py, mt, mb, mx, my, m, withBorder, shadow, children,
  bg, c, bd, w, h, maw, miw, mah, pos, top, right, bottom, left,
  display, flex, ta, fz, fw, component, padding,
  style, className = '', ...rest
}: CardProps) {
  const Tag = (component as any) ?? 'div';
  const actualPadding = padding ?? p;
  const s: CSSProperties = {
    backgroundColor: bg ?? 'white',
    borderRadius: 'var(--radius-lg)',
    boxShadow: shadow === 'none' ? 'none' : 'var(--shadow-md)',
    ...(withBorder ? { border: bd ?? '1px solid var(--color-neutral-200)' } : bd ? { border: bd } : {}),
    ...(actualPadding ? { padding: sp(actualPadding) } : {}),
    ...(pt ? { paddingTop: sp(pt) } : {}),
    ...(pb ? { paddingBottom: sp(pb) } : {}),
    ...(px ? { paddingLeft: sp(px), paddingRight: sp(px) } : {}),
    ...(py ? { paddingTop: sp(py), paddingBottom: sp(py) } : {}),
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(m ? { margin: sp(m) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(h ? { height: typeof h === 'number' ? `${h}px` : h } : {}),
    ...(maw ? { maxWidth: typeof maw === 'number' ? `${maw}px` : maw } : {}),
    ...(miw ? { minWidth: typeof miw === 'number' ? `${miw}px` : miw } : {}),
    ...(mah ? { maxHeight: typeof mah === 'number' ? `${mah}px` : mah } : {}),
    ...(pos ? { position: pos } : {}),
    ...(top !== undefined ? { top: sp(top) } : {}),
    ...(right !== undefined ? { right: sp(right) } : {}),
    ...(bottom !== undefined ? { bottom: sp(bottom) } : {}),
    ...(left !== undefined ? { left: sp(left) } : {}),
    ...(display ? { display } : {}),
    ...(flex !== undefined ? { flex } : {}),
    ...(ta ? { textAlign: ta } : {}),
    ...(fz ? { fontSize: resolveFontSize(fz) } : {}),
    ...(fw ? { fontWeight: fw } : {}),
    ...(c ? { color: TEXT_COLOR[c] ?? c } : {}),
    ...style,
  };
  return <Tag className={`card ${className}`} style={s} {...rest}>{children}</Tag>;
}

function CardSection({ children, withBorder, mt, mb, mx, my, style, className = '', ...rest }: {
  children?: ReactNode; withBorder?: boolean;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  style?: CSSProperties; className?: string;
} & HTMLAttributes<HTMLDivElement>) {
  const s: CSSProperties = {
    margin: '0 -1rem',
    ...(withBorder ? { borderTop: '1px solid var(--color-neutral-200)', borderBottom: '1px solid var(--color-neutral-200)' } : {}),
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...style,
  };
  return <div className={className} style={s} {...rest}>{children}</div>;
}

export const Card = Object.assign(CardRoot, { Section: CardSection });

// ── Combobox & useCombobox ─────────────────────────────────

// Simple no-op implementations — components that use Combobox should
// ideally be converted to use GabSelect, but this prevents build failure.

interface ComboboxStore {
  dropdownOpened: boolean;
  openDropdown: () => void;
  closeDropdown: () => void;
  toggleDropdown: () => void;
  selectedOptionIndex: number;
  focusTarget: () => void;
  resetSelectedOption: () => void;
  selectFirstOption: () => void;
  selectActiveOption: () => void;
  selectNextOption: () => void;
  selectPreviousOption: () => void;
  clickSelectedOption: () => void;
}

export function useCombobox(opts?: { onDropdownClose?: () => void; onDropdownOpen?: () => void }): ComboboxStore {
  const [opened, setOpened] = useState(false);
  return {
    dropdownOpened: opened,
    openDropdown: () => { setOpened(true); opts?.onDropdownOpen?.(); },
    closeDropdown: () => { setOpened(false); opts?.onDropdownClose?.(); },
    toggleDropdown: () => setOpened(o => !o),
    selectedOptionIndex: -1,
    focusTarget: () => {},
    resetSelectedOption: () => {},
    selectFirstOption: () => {},
    selectActiveOption: () => {},
    selectNextOption: () => {},
    selectPreviousOption: () => {},
    clickSelectedOption: () => {},
  };
}

interface ComboboxProps {
  store: ComboboxStore;
  onOptionSubmit?: (value: string) => void;
  children?: ReactNode;
  withinPortal?: boolean;
  position?: string;
  offset?: number;
  width?: string | number;
  shadow?: string;
  transitionProps?: Record<string, unknown>;
  styles?: Record<string, unknown>;
  classNames?: Record<string, string>;
  size?: MantineSize;
  readOnly?: boolean;
  resetSelectionOnOptionHover?: boolean;
  keepMounted?: boolean;
  __staticSelector?: string;
  dropdownPadding?: number;
  style?: CSSProperties;
}

function ComboboxRoot({ children, store, onOptionSubmit }: ComboboxProps) {
  return (
    <div style={{ position: 'relative' }}
      onKeyDown={e => { if (e.key === 'Escape') store.closeDropdown(); }}>
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) return child;
        return React.cloneElement(child as React.ReactElement<any>, { _store: store, _onOptionSubmit: onOptionSubmit });
      })}
    </div>
  );
}

const ComboboxTarget = ({ children, _store: _s, _onOptionSubmit: _o, refProp: _r, ...rest }: any) => <>{children}</>;
ComboboxTarget.displayName = 'ComboboxTarget';

const ComboboxDropdown = ({ children, _store, hidden, style, className = '' }: any) => {
  if (!_store?.dropdownOpened || hidden) return null;
  return (
    <div className={className} style={{
      position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 1000,
      background: 'white', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
      border: '1px solid var(--color-neutral-200)', ...style,
    }}>
      {children}
    </div>
  );
};
const ComboboxDropdownTarget = ({ children }: any) => <>{children}</>;
const ComboboxOptions = ({ children, style, className = '' }: any) => (
  <div className={className} style={{ maxHeight: 240, overflowY: 'auto', ...style }} role="listbox">{children}</div>
);
const ComboboxOption = ({ children, value, _onOptionSubmit, style, className = '', active }: any) => (
  <div role="option" aria-selected={active ?? false} className={className}
    style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: 'var(--font-size-sm)', background: active ? 'var(--color-primary-50)' : 'white', ...style }}
    onClick={() => _onOptionSubmit?.(value)}>
    {children}
  </div>
);
const ComboboxEmpty = ({ children, style, className = '' }: any) => (
  <div className={className} style={{ padding: '0.5rem 0.75rem', color: 'var(--color-neutral-500)', fontSize: 'var(--font-size-sm)', textAlign: 'center', ...style }}>{children}</div>
);
const ComboboxGroup = ({ children, label, style, className = '' }: any) => (
  <div className={className} style={style}>
    {label && <div style={{ padding: '0.25rem 0.75rem', fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-neutral-500)' }}>{label}</div>}
    {children}
  </div>
);
const ComboboxFooter = ({ children, style, className = '' }: any) => (
  <div className={className} style={{ borderTop: '1px solid var(--color-neutral-200)', padding: '0.5rem 0.75rem', ...style }}>{children}</div>
);
const ComboboxHeader = ({ children, style, className = '' }: any) => (
  <div className={className} style={{ borderBottom: '1px solid var(--color-neutral-200)', padding: '0.5rem 0.75rem', ...style }}>{children}</div>
);
const ComboboxSearch = ({ style, className = '', ...rest }: any) => (
  <div style={{ padding: '0.25rem 0.5rem' }}>
    <input type="text" className={`form-input ${className}`} style={style} {...rest} />
  </div>
);
const ComboboxChevron = ({ size: _s, error: _e, style, ...rest }: any) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" style={style} {...rest}><path d="M6 9L1 4h10z"/></svg>
);
const ComboboxClearButton = ({ onClear, style, ...rest }: any) => (
  <button type="button" onClick={onClear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-neutral-400)', padding: 0, ...style }} {...rest}>&times;</button>
);

export const Combobox = Object.assign(ComboboxRoot, {
  Target: ComboboxTarget,
  Dropdown: ComboboxDropdown,
  DropdownTarget: ComboboxDropdownTarget,
  Options: ComboboxOptions,
  Option: ComboboxOption,
  Empty: ComboboxEmpty,
  Group: ComboboxGroup,
  Footer: ComboboxFooter,
  Header: ComboboxHeader,
  Search: ComboboxSearch,
  Chevron: ComboboxChevron,
  ClearButton: ComboboxClearButton,
});

// ── ColorInput ────────────────────────────────────────────

interface ColorInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'onChange' | 'value'> {
  value?: string;
  onChange?: (value: string) => void;
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  format?: string;
  withPicker?: boolean;
  withEyeDropper?: boolean;
  swatches?: string[];
  size?: MantineSize;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  w?: MantineSize; flex?: string | number;
  withAsterisk?: boolean;
  styles?: Record<string, unknown>;
  fixOnBlur?: boolean;
}

export function ColorInput({
  label, description, error, required, value, onChange, withAsterisk,
  mt, mb, mx, my, w, flex, style, className = '', id,
  format: _fmt, withPicker: _wp, withEyeDropper: _we, swatches: _sw,
  size: _size, styles: _styles, fixOnBlur: _fob,
  ...rest
}: ColorInputProps) {
  const inputId = id || (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  const wrapStyle: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(flex !== undefined ? { flex } : {}),
  };
  return (
    <div className={`form-group ${className}`} style={wrapStyle}>
      {label && <label className={`form-label${required || withAsterisk ? ' required' : ''}`} htmlFor={inputId}>{label}</label>}
      {description && <span className="form-help">{description}</span>}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <input type="color" value={value || '#000000'} onChange={e => onChange?.(e.target.value)} style={{ width: 40, height: 38, borderRadius: 'var(--radius-md)', border: '1px solid var(--color-neutral-300)', cursor: 'pointer', padding: 2 }} />
        <input
          id={inputId}
          className={`form-input${error ? ' error' : ''}`}
          value={value || ''}
          onChange={e => onChange?.(e.target.value)}
          style={{ flex: 1, ...style }}
          {...rest}
        />
      </div>
      {error && <span className="form-error" role="alert">{error}</span>}
    </div>
  );
}

// ── Kbd ───────────────────────────────────────────────────

interface KbdProps {
  children?: ReactNode;
  size?: MantineSize;
  style?: CSSProperties;
  className?: string;
}

export function Kbd({ children, style, className = '' }: KbdProps) {
  return (
    <kbd className={className} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      padding: '0.1rem 0.4rem', fontSize: '0.8em',
      fontFamily: 'var(--font-mono)',
      backgroundColor: 'var(--color-neutral-100)',
      border: '1px solid var(--color-neutral-300)',
      borderRadius: 'var(--radius-sm)',
      boxShadow: '0 1px 0 var(--color-neutral-300)',
      color: 'var(--color-neutral-700)',
      ...style,
    }}>
      {children}
    </kbd>
  );
}

// ── Notification re-export ────────────────────────────────

export { Notifications } from '../../lib/notifications';

// ── Progress compound (Root / Section / Label) ────────────

interface ProgressRootProps { size?: MantineSize; radius?: MantineSize; mt?: MantineSize; mb?: MantineSize; children?: React.ReactNode; style?: CSSProperties; className?: string; }
function ProgressRoot({ size, mt, mb, children, style, className = '' }: ProgressRootProps) {
  const height = size === 'lg' ? 20 : size === 'md' ? 14 : size === 'sm' ? 10 : size === 'xs' ? 6 : 14;
  return (
    <div className={`progress-root ${className}`} style={{ display: 'flex', width: '100%', height, borderRadius: 999, overflow: 'hidden', background: 'var(--color-neutral-200)', ...(mt ? { marginTop: sp(mt) } : {}), ...(mb ? { marginBottom: sp(mb) } : {}), ...style }}>
      {children}
    </div>
  );
}

interface ProgressSectionProps { value: number; color?: string; children?: React.ReactNode; style?: CSSProperties; }
function ProgressSection({ value, color, children, style }: ProgressSectionProps) {
  const bg = color ? `var(--color-${color}-600, ${color})` : 'var(--color-primary-600)';
  return (
    <div style={{ width: `${Math.min(100, Math.max(0, value))}%`, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', transition: 'width 0.3s', ...style }}>
      {children}
    </div>
  );
}

function ProgressLabel({ children }: { children?: React.ReactNode }) {
  return <span style={{ fontSize: 10, color: '#fff', fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap', padding: '0 4px' }}>{children}</span>;
}

// ── Progress compound components (Root / Section / Label) ────
// Exported as a typed namespace via Object.assign so TypeScript
// can see Progress.Root, Progress.Section, Progress.Label.
export const Progress = Object.assign(ProgressComponent, {
  Root: ProgressRoot,
  Section: ProgressSection,
  Label: ProgressLabel,
});

// ── Checkbox.Group ────────────────────────────────────────

interface CheckboxGroupShimProps {
  value?: string[];
  defaultValue?: string[];
  onChange?: (value: string[]) => void;
  label?: React.ReactNode;
  description?: React.ReactNode;
  error?: React.ReactNode;
  children?: React.ReactNode;
  mt?: MantineSize; mb?: MantineSize;
  style?: CSSProperties;
}
function CheckboxGroupShim({ value, defaultValue, onChange, label, description, error, children, mt, mb, style }: CheckboxGroupShimProps) {
  const [internal, setInternal] = React.useState<string[]>(defaultValue ?? []);
  const controlled = value !== undefined;
  const current = controlled ? value! : internal;
  const handleChange = (v: string, checked: boolean) => {
    const next = checked ? [...current, v] : current.filter(x => x !== v);
    if (!controlled) setInternal(next);
    onChange?.(next);
  };
  const enhanced = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return child;
    const c = child as React.ReactElement<any>;
    const val = c.props.value ?? '';
    return React.cloneElement(c, { checked: current.includes(val), onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange(val, e.target.checked) });
  });
  return (
    <div style={{ ...(mt ? { marginTop: sp(mt) } : {}), ...(mb ? { marginBottom: sp(mb) } : {}), ...style }}>
      {label && <div className="form-label">{label}</div>}
      {description && <span className="form-help">{description}</span>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: label ? '0.25rem' : 0 }}>{enhanced}</div>
      {error && <span className="form-error">{error}</span>}
    </div>
  );
}

// Typed compound export — TypeScript can see Checkbox.Group
export const Checkbox = Object.assign(CheckboxBase, { Group: CheckboxGroupShim });

// ── Chip.Group ────────────────────────────────────────────

interface ChipGroupProps {
  multiple?: boolean;
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  children?: React.ReactNode;
}
function ChipGroup({ multiple, value, onChange, children }: ChipGroupProps) {
  const toggle = (v: string) => {
    if (multiple) {
      const arr = (Array.isArray(value) ? value : []) as string[];
      const next = arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];
      (onChange as ((v: string[]) => void))?.(next);
    } else {
      (onChange as ((v: string) => void))?.(v === value ? '' : v);
    }
  };
  const isActive = (v: string) => multiple ? (Array.isArray(value) && value.includes(v)) : value === v;
  const enhanced = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return child;
    const c = child as React.ReactElement<any>;
    const val = c.props.value ?? '';
    return React.cloneElement(c, { checked: isActive(val), onChange: () => toggle(val) });
  });
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>{enhanced}</div>;
}

// Typed compound export — TypeScript can see Chip.Group
export const Chip = Object.assign(ChipComponent, { Group: ChipGroup });

// ── Button.Group ──────────────────────────────────────────

interface ButtonGroupProps { children?: React.ReactNode; orientation?: 'horizontal' | 'vertical'; style?: CSSProperties; }
function ButtonGroup({ children, orientation = 'horizontal', style }: ButtonGroupProps) {
  return (
    <div style={{ display: 'inline-flex', flexDirection: orientation === 'vertical' ? 'column' : 'row', ...style }}>
      {children}
    </div>
  );
}

// Typed compound export — TypeScript can see Button.Group
export const Button = Object.assign(ButtonBase, { Group: ButtonGroup });
